import {
  createViewportEvidenceIdentity,
  createViewportRequestOrdinalAllocatorAdapter,
} from './viewportEvidenceIdentity';
import {
  claimVisibleJpegValidatedPreloadRoot,
  loadVisibleJpegNetworkFrame,
} from './viewportVisibleJpegNetworkLoader';
import { createViewportCineInteractionController } from './viewportCineInteraction';

const REPORT_SESSION_STATES = new WeakMap();
const OPERATION_STATES = new WeakMap();
const CACHE_ROOT_STATES = new WeakMap();
const WINDOW_LEVEL_CLOCK_STATES = new WeakMap();
const WINDOW_LEVEL_TEST_EVENT_STATES = new WeakMap();
const WINDOW_LEVEL_INTERACTION_STATES = new WeakMap();
const WINDOW_LEVEL_INPUT_STATES = new WeakMap();
const WINDOW_LEVEL_PUBLICATION_STATES = new WeakMap();
const CINE_AUTHORIZATION_STATES = new WeakMap();
const CINE_PHASE_STATES = new WeakMap();
const CINE_INTERACTION_PHASE_STATES = new WeakMap();
const CINE_DIAGNOSTIC_LEDGER_STATES = new WeakMap();
const CINE_ACTIVITY_RECEIPT_STATES = new WeakMap();
const CINE_TEST_FRAME_SCHEDULER_STATES = new WeakMap();
const CINE_FRAME_REQUEST_STATES = new WeakMap();
const RETAINED_EVENT_ROWS = new WeakSet();
const RETAINED_EVENT_DRAINS = new WeakSet();
const DERIVED_CINE_PACKAGES = new WeakMap();
const WINDOW_LEVEL_TEST_CAPABILITIES_ENABLED = typeof process !== 'undefined'
  && process.env?.NODE_ENV === 'test';
const BROWSER_FRAME_SCHEDULER = (() => {
  try {
    if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function'
        || typeof window.cancelAnimationFrame !== 'function') return null;
    const realm = window;
    const request = realm.requestAnimationFrame;
    const cancel = realm.cancelAnimationFrame;
    return Object.freeze({
      request: callback => Reflect.apply(request, realm, [callback]),
      cancel: frameId => Reflect.apply(cancel, realm, [frameId]),
    });
  } catch (_) {
    return null;
  }
})();
const SAFE_SURFACES = new Set(['preview', 'active-image']);
const BEGIN_KEYS = Object.freeze([
  'benchmarkMode',
  'context',
  'requestUrl',
  'currentOrigin',
  'fetchImpl',
  'blobFactory',
  'createObjectURL',
  'revokeObjectURL',
  'createImage',
  'isMounted',
  'isViewportCurrent',
  'isGenerationCurrent',
  'isSliceCurrent',
  'isRenderAuthorityCurrent',
]);
const WARM_BEGIN_KEYS = Object.freeze([
  'benchmarkMode',
  'context',
  'canonicalUrl',
  'cacheRoot',
  'isMounted',
  'isViewportCurrent',
  'isGenerationCurrent',
  'isSliceCurrent',
  'isRenderAuthorityCurrent',
]);
const EVIDENCE_CONTEXT_KEYS = Object.freeze([
  'viewport',
  'generation',
  'slice',
  'phase',
  'interaction',
  'temperature',
  'deliverySource',
]);

let installedSession = null;
let installedCinePhase = null;
let installedCineTestFrameScheduler = null;
let nextEvidenceOrdinal = 1;
let nextRequestOrdinal = 1;
let nextCacheRootOrdinal = 1;
let nextConsumerOrdinal = 1;
let nextWindowLevelInteractionOrdinal = 1;
let nextWindowLevelInputOrdinal = 1;
let nextCineOrdinal = 1;
let nextCineInputOrdinal = 1;
let nextCineBrowserOrdinal = 1;
let nextCineWorkOrdinal = 1;
let nextCineGenerationOrdinal = 1;
let nextCineActivityOrdinal = 1;
let nextCinePendingRenderOrdinal = 1;

export const isViewportEvidenceBenchmarkRequested = () => {
  try {
    if (typeof window === 'undefined') return false;
    const fixedFlag = Object.getOwnPropertyDescriptor(
      window,
      '__KAIROS_V8_EVIDENCE_BENCHMARK_ACTIVE__',
    );
    if (fixedFlag && Object.prototype.hasOwnProperty.call(fixedFlag, 'value')
        && fixedFlag.value === true) return true;
    const values = new URL(window.location.href).searchParams.getAll('kairosEvidenceBenchmark');
    return values.length === 1 && values[0] === '1';
  } catch (_) {
    return false;
  }
};

export const isViewportEvidenceNetworkBridgeActive = () => {
  let state = null;
  try { state = installedSession && REPORT_SESSION_STATES.get(installedSession); } catch (_) { state = null; }
  return Boolean(state && !state.closed && isViewportEvidenceBenchmarkRequested());
};

const reserveGlobalOrdinal = kind => {
  const current = {
    evidence: nextEvidenceOrdinal,
    request: nextRequestOrdinal,
    cacheRoot: nextCacheRootOrdinal,
    consumer: nextConsumerOrdinal,
    windowLevelInteraction: nextWindowLevelInteractionOrdinal,
    windowLevelInput: nextWindowLevelInputOrdinal,
    cine: nextCineOrdinal,
    cineInput: nextCineInputOrdinal,
    cineBrowser: nextCineBrowserOrdinal,
    cineWork: nextCineWorkOrdinal,
    cineGeneration: nextCineGenerationOrdinal,
    cineActivity: nextCineActivityOrdinal,
    cinePendingRender: nextCinePendingRenderOrdinal,
  }[kind];
  if (!Number.isSafeInteger(current) || current <= 0) throw new Error('Report ordinal space is exhausted');
  if (kind === 'evidence') nextEvidenceOrdinal = current === Number.MAX_SAFE_INTEGER ? null : current + 1;
  else if (kind === 'request') nextRequestOrdinal = current === Number.MAX_SAFE_INTEGER ? null : current + 1;
  else if (kind === 'cacheRoot') nextCacheRootOrdinal = current === Number.MAX_SAFE_INTEGER ? null : current + 1;
  else if (kind === 'consumer') nextConsumerOrdinal = current === Number.MAX_SAFE_INTEGER ? null : current + 1;
  else if (kind === 'windowLevelInteraction') nextWindowLevelInteractionOrdinal = current === Number.MAX_SAFE_INTEGER ? null : current + 1;
  else if (kind === 'windowLevelInput') nextWindowLevelInputOrdinal = current === Number.MAX_SAFE_INTEGER ? null : current + 1;
  else if (kind === 'cine') nextCineOrdinal = current === Number.MAX_SAFE_INTEGER ? null : current + 1;
  else if (kind === 'cineInput') nextCineInputOrdinal = current === Number.MAX_SAFE_INTEGER ? null : current + 1;
  else if (kind === 'cineBrowser') nextCineBrowserOrdinal = current === Number.MAX_SAFE_INTEGER ? null : current + 1;
  else if (kind === 'cineWork') nextCineWorkOrdinal = current === Number.MAX_SAFE_INTEGER ? null : current + 1;
  else if (kind === 'cineGeneration') nextCineGenerationOrdinal = current === Number.MAX_SAFE_INTEGER ? null : current + 1;
  else if (kind === 'cineActivity') nextCineActivityOrdinal = current === Number.MAX_SAFE_INTEGER ? null : current + 1;
  else if (kind === 'cinePendingRender') nextCinePendingRenderOrdinal = current === Number.MAX_SAFE_INTEGER ? null : current + 1;
  else throw new Error('Report ordinal kind is invalid');
  return current;
};

const safeContext = descriptor => Object.freeze({
  viewport: descriptor.viewport,
  generation: descriptor.generation,
  slice: descriptor.slice,
  phase: descriptor.phase,
  interaction: descriptor.interaction,
  temperature: descriptor.temperature,
  deliverySource: descriptor.deliverySource,
});

const appendRetainedEvent = (state, context, event) => {
  if (!state || state.draining) return null;
  const retained = Object.freeze({
    sequence: state.nextSequence,
    ...context,
    ...event,
  });
  RETAINED_EVENT_ROWS.add(retained);
  state.nextSequence += 1;
  state.events.push(retained);
  return retained;
};

// Preserve the established boolean helper contract for every existing caller.
const appendEvent = (state, context, event) => appendRetainedEvent(state, context, event) !== null;

const preserveCleanup = (sessionState, retry) => {
  if (typeof retry !== 'function') return false;
  let job;
  job = () => {
    let cleaned = false;
    try { cleaned = retry() === true; } catch (_) { cleaned = false; }
    if (cleaned) sessionState.pendingCleanup.delete(job);
    return cleaned;
  };
  sessionState.pendingCleanup.add(job);
  return true;
};

const retryPendingCleanup = state => {
  let cleaned = false;
  [...state.pendingCleanup].forEach(job => {
    if (job()) cleaned = true;
  });
  return cleaned;
};

const preserveCapabilityCleanupIfNeeded = (sessionState, capability) => {
  let snapshot = null;
  try { snapshot = capability?.snapshot?.(); } catch (_) { snapshot = null; }
  if (snapshot?.revocationPending !== true) return false;
  preserveCleanup(sessionState, () => capability.release());
  return true;
};

const appendPreloadTerminal = (operationState, outcome) => {
  if (!operationState?.preload || operationState.preloadTerminal) return false;
  operationState.preloadTerminal = true;
  appendEvent(operationState.sessionState, operationState.context, {
    type: 'preload-terminal',
    evidenceOrdinal: operationState.evidenceOrdinal,
    requestOrdinal: operationState.requestOrdinal,
    outcome,
  });
  return true;
};

const appendPreloadCleanup = (rootState, outcome) => {
  if (!rootState?.preload || rootState.cleanupComplete) return false;
  if (outcome === 'complete') rootState.cleanupComplete = true;
  appendEvent(rootState.sessionState, rootState.context, {
    type: 'preload-cleanup',
    evidenceOrdinal: rootState.evidenceOrdinal,
    requestOrdinal: rootState.requestOrdinal,
    cacheRootOrdinal: rootState.cacheRootOrdinal,
    outcome,
  });
  return true;
};

const maybeReleaseCacheRootFrame = rootState => {
  if (!rootState.evicted || rootState.displayOwned || rootState.frameReleased
      || rootState.reservations > 0 || rootState.consumers.size > 0) return true;
  rootState.frameReleased = true;
  let released = false;
  try { released = rootState.frame.release() === true; } catch (_) { released = false; }
  if (!released) {
    if (rootState.preload) {
      let pending = false;
      try { pending = rootState.frame.snapshot()?.revocationPending === true; } catch (_) { pending = false; }
      appendPreloadCleanup(rootState, pending ? 'pending' : 'complete');
      if (pending) {
        preserveCleanup(rootState.sessionState, () => {
          let cleaned = false;
          try { cleaned = rootState.frame.release() === true; } catch (_) { cleaned = false; }
          if (cleaned) appendPreloadCleanup(rootState, 'complete');
          return cleaned;
        });
      }
    } else preserveCapabilityCleanupIfNeeded(rootState.sessionState, rootState.frame);
  } else if (rootState.preload) appendPreloadCleanup(rootState, 'complete');
  return released;
};

const evictCacheRootState = rootState => {
  if (!rootState || rootState.evicted) return false;
  rootState.evicted = true;
  rootState.sessionState.roots.delete(rootState.root);
  if (rootState.cacheDetach && !rootState.cacheDetached) {
    const detach = rootState.cacheDetach;
    rootState.cacheDetach = null;
    rootState.cacheDetached = true;
    try { Reflect.apply(detach, undefined, []); } catch (_) {}
  }
  maybeReleaseCacheRootFrame(rootState);
  return true;
};

const evictProvisionalCacheRoot = operationState => {
  const rootState = operationState.provisionalCacheRootState;
  if (!rootState) return false;
  operationState.provisionalCacheRootState = null;
  return evictCacheRootState(rootState);
};

const finishOperation = operationState => {
  if (operationState.finished) return false;
  if (operationState.cineCommandState) {
    viewportCineController.invalidateRenderOperation(
      operationState.cineCommandState,
      operationState.operation,
    );
  }
  operationState.finished = true;
  if (operationState.cacheRootState) {
    operationState.cacheRootState.consumers.delete(operationState.operation);
    maybeReleaseCacheRootFrame(operationState.cacheRootState);
  }
  operationState.sessionState.operations.delete(operationState.operation);
  return true;
};

const preserveFrameCleanupIfNeeded = operationState => (
  preserveCapabilityCleanupIfNeeded(operationState.sessionState, operationState.frame)
);

const preserveOperationReleaseCleanup = operationState => {
  if (operationState.releaseCleanupPreserved) return false;
  operationState.releaseCleanupPreserved = true;
  preserveCleanup(operationState.sessionState, () => {
    const released = releaseFrame(operationState);
    if (released || operationState.frameReleased) operationState.releaseCleanupPreserved = false;
    return released || operationState.frameReleased;
  });
  return true;
};

const releaseFrame = operationState => {
  if (!operationState.frame || operationState.frameReleased) return false;
  if (operationState.relinquish && !operationState.relinquished) {
    let relinquished = false;
    try { relinquished = operationState.relinquish() === true; } catch (_) { relinquished = false; }
    if (!relinquished) {
      preserveOperationReleaseCleanup(operationState);
      return false;
    }
    operationState.relinquished = true;
  }
  operationState.frameReleased = true;
  if (operationState.coldCacheRootState) {
    operationState.coldCacheRootState.displayOwned = false;
    return maybeReleaseCacheRootFrame(operationState.coldCacheRootState);
  }
  let released = false;
  try { released = operationState.frame.release() === true; } catch (_) { released = false; }
  if (!released) preserveFrameCleanupIfNeeded(operationState);
  return released;
};

const cancelOperation = operationState => {
  if (operationState.cancelled && operationState.finished) return false;
  const changed = !operationState.cancelled;
  operationState.cancelled = true;
  if (operationState.preload) appendPreloadTerminal(operationState, 'cancelled');
  try { operationState.controller.abort(); } catch (_) {}
  evictProvisionalCacheRoot(operationState);
  if (operationState.frame) {
    try { operationState.frame.cancel(); } catch (_) {}
    releaseFrame(operationState);
  }
  finishOperation(operationState);
  return changed;
};

const WINDOW_LEVEL_START_KEYS = Object.freeze([
  'event', 'clock', 'phase', 'viewport', 'generation', 'windowLevel', 'isAuthorityCurrent',
]);
const WINDOW_LEVEL_INPUT_KEYS = Object.freeze(['event', 'windowLevel']);
const WINDOW_LEVEL_RENDER_BINDING_KEYS = Object.freeze(['input', 'windowLevel']);
const WINDOW_LEVEL_PHASES = new Set(['single-step']);

const snapshotExactDataObject = (value, keys) => {
  try {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    const ownKeys = Reflect.ownKeys(value);
    if (ownKeys.length !== keys.length
        || ownKeys.some(key => typeof key !== 'string' || !keys.includes(key))) return null;
    const snapshot = Object.create(null);
    for (const key of keys) {
      const descriptor = Object.getOwnPropertyDescriptor(value, key);
      if (!descriptor || !Object.prototype.hasOwnProperty.call(descriptor, 'value')) return null;
      snapshot[key] = descriptor.value;
    }
    return Object.freeze(snapshot);
  } catch (_) {
    return null;
  }
};

const snapshotWindowLevel = value => {
  const snapshot = snapshotExactDataObject(value, ['ww', 'wc']);
  if (!snapshot || typeof snapshot.ww !== 'number' || typeof snapshot.wc !== 'number'
      || !Number.isFinite(snapshot.ww) || !Number.isFinite(snapshot.wc)) return null;
  return Object.freeze({
    ww: Math.max(0, Math.min(9999, Math.round(snapshot.ww))),
    wc: Math.max(-9999, Math.min(9999, Math.round(snapshot.wc))),
  });
};

const sameWindowLevel = (left, right) => Boolean(left && right
  && left.ww === right.ww && left.wc === right.wc);

const findOwnDescriptor = (value, key) => {
  let current = value;
  while (current) {
    const descriptor = Object.getOwnPropertyDescriptor(current, key);
    if (descriptor) return descriptor;
    current = Object.getPrototypeOf(current);
  }
  return null;
};

const captureBrowserWindowLevelBoundary = () => {
  try {
    if (typeof window === 'undefined') return null;
    const realm = window;
    const EventConstructor = realm.Event;
    const MouseEventConstructor = realm.MouseEvent;
    const PointerEventConstructor = typeof realm.PointerEvent === 'function' ? realm.PointerEvent : null;
    const WheelEventConstructor = realm.WheelEvent;
    const InputEventConstructor = typeof realm.InputEvent === 'function' ? realm.InputEvent : null;
    const HTMLInputElementConstructor = realm.HTMLInputElement;
    if (typeof EventConstructor !== 'function' || typeof MouseEventConstructor !== 'function'
        || typeof WheelEventConstructor !== 'function'
        || typeof HTMLInputElementConstructor !== 'function') return null;
    const eventPrototype = EventConstructor.prototype;
    const mouseEventPrototype = MouseEventConstructor.prototype;
    const pointerEventPrototype = PointerEventConstructor?.prototype || null;
    const wheelEventPrototype = WheelEventConstructor.prototype;
    const inputEventPrototype = InputEventConstructor?.prototype || null;
    const htmlInputElementPrototype = HTMLInputElementConstructor.prototype;
    const typeDescriptor = findOwnDescriptor(eventPrototype, 'type');
    const targetDescriptor = findOwnDescriptor(eventPrototype, 'target');
    const mouseButtonDescriptor = findOwnDescriptor(mouseEventPrototype, 'button');
    const mouseButtonsDescriptor = findOwnDescriptor(mouseEventPrototype, 'buttons');
    const mouseCtrlKeyDescriptor = findOwnDescriptor(mouseEventPrototype, 'ctrlKey');
    const wheelDeltaXDescriptor = findOwnDescriptor(wheelEventPrototype, 'deltaX');
    const wheelDeltaYDescriptor = findOwnDescriptor(wheelEventPrototype, 'deltaY');
    const wheelDeltaZDescriptor = findOwnDescriptor(wheelEventPrototype, 'deltaZ');
    const pointerButtonDescriptor = pointerEventPrototype
      ? findOwnDescriptor(pointerEventPrototype, 'button') : null;
    const pointerButtonsDescriptor = pointerEventPrototype
      ? findOwnDescriptor(pointerEventPrototype, 'buttons') : null;
    const inputTypeDescriptor = findOwnDescriptor(htmlInputElementPrototype, 'type');
    if (typeof typeDescriptor?.get !== 'function' || typeDescriptor.set !== undefined
        || typeof targetDescriptor?.get !== 'function' || targetDescriptor.set !== undefined
        || typeof inputTypeDescriptor?.get !== 'function'
        || typeof mouseButtonDescriptor?.get !== 'function' || mouseButtonDescriptor.set !== undefined
        || typeof mouseButtonsDescriptor?.get !== 'function' || mouseButtonsDescriptor.set !== undefined
        || typeof mouseCtrlKeyDescriptor?.get !== 'function' || mouseCtrlKeyDescriptor.set !== undefined
        || typeof wheelDeltaXDescriptor?.get !== 'function' || wheelDeltaXDescriptor.set !== undefined
        || typeof wheelDeltaYDescriptor?.get !== 'function' || wheelDeltaYDescriptor.set !== undefined
        || typeof wheelDeltaZDescriptor?.get !== 'function' || wheelDeltaZDescriptor.set !== undefined
        || (pointerEventPrototype && (typeof pointerButtonDescriptor?.get !== 'function'
          || pointerButtonDescriptor.set !== undefined
          || typeof pointerButtonsDescriptor?.get !== 'function'
          || pointerButtonsDescriptor.set !== undefined))) return null;

    const trustedPrototypeDescriptor = findOwnDescriptor(eventPrototype, 'isTrusted');
    let trustedOwnShape = null;
    let testRealmImplementationKey = null;
    let testRealmWrapperKey = null;
    if (typeof trustedPrototypeDescriptor?.get !== 'function') {
      const probe = Reflect.construct(EventConstructor, ['__kairos_trust_probe__']);
      const descriptor = Object.getOwnPropertyDescriptor(probe, 'isTrusted');
      if (!descriptor || typeof descriptor.get !== 'function' || descriptor.set !== undefined
          || Object.prototype.hasOwnProperty.call(descriptor, 'value')) return null;
      trustedOwnShape = Object.freeze({
        enumerable: descriptor.enumerable,
        configurable: descriptor.configurable,
      });
      // jsdom exposes its native wrapper/implementation link as non-enumerable
      // symbol data. Capture that controlled probe link only to keep proxy tests
      // faithful: a Proxy around a native event points back to the wrapped event,
      // not to the Proxy. Real browsers expose no such keys and use native slots.
      for (const key of Reflect.ownKeys(probe)) {
        if (typeof key !== 'symbol') continue;
        const implementationDescriptor = Object.getOwnPropertyDescriptor(probe, key);
        const implementation = implementationDescriptor?.value;
        if (!implementation || typeof implementation !== 'object') continue;
        for (const wrapperKey of Reflect.ownKeys(implementation)) {
          const wrapperDescriptor = Object.getOwnPropertyDescriptor(implementation, wrapperKey);
          if (wrapperDescriptor?.value === probe) {
            testRealmImplementationKey = key;
            testRealmWrapperKey = wrapperKey;
            break;
          }
        }
        if (testRealmImplementationKey) break;
      }
    }

    const performanceApi = realm.performance;
    const performancePrototype = performanceApi && Object.getPrototypeOf(performanceApi);
    const nowDescriptor = performancePrototype && findOwnDescriptor(performancePrototype, 'now');
    if (!performanceApi || Object.getOwnPropertyDescriptor(performanceApi, 'now')
        || !nowDescriptor || typeof nowDescriptor.value !== 'function'
        || nowDescriptor.writable !== true) return null;

    return Object.freeze({
      EventConstructor,
      MouseEventConstructor,
      PointerEventConstructor,
      WheelEventConstructor,
      InputEventConstructor,
      HTMLInputElementConstructor,
      eventPrototype,
      mouseEventPrototype,
      pointerEventPrototype,
      wheelEventPrototype,
      inputEventPrototype,
      htmlInputElementPrototype,
      readType: typeDescriptor.get,
      readTarget: targetDescriptor.get,
      readMouseButton: mouseButtonDescriptor.get,
      readMouseButtons: mouseButtonsDescriptor.get,
      readMouseCtrlKey: mouseCtrlKeyDescriptor.get,
      readWheelDeltaX: wheelDeltaXDescriptor.get,
      readWheelDeltaY: wheelDeltaYDescriptor.get,
      readWheelDeltaZ: wheelDeltaZDescriptor.get,
      readPointerButton: pointerButtonDescriptor?.get || null,
      readPointerButtons: pointerButtonsDescriptor?.get || null,
      readTrusted: trustedPrototypeDescriptor?.get || null,
      readInputType: inputTypeDescriptor.get,
      trustedOwnShape,
      testRealmImplementationKey,
      testRealmWrapperKey,
      performanceApi,
      readNow: nowDescriptor.value,
    });
  } catch (_) {
    return null;
  }
};

const primaryButtonLifecycleMatches = (type, button, buttons) => {
  if (!Number.isSafeInteger(button) || !Number.isSafeInteger(buttons) || buttons < 0) return false;
  const primaryHeld = (buttons & 1) === 1;
  if (type === 'pointerdown' || type === 'mousedown') return button === 0 && primaryHeld;
  if (type === 'pointermove') return button === -1 && primaryHeld;
  if (type === 'mousemove') return button === 0 && primaryHeld;
  if (type === 'pointerup' || type === 'mouseup') return button === 0 && !primaryHeld;
  return false;
};

const trustedOwnDescriptorMatchesBoundary = (descriptor, trustedOwnShape) => Boolean(
  descriptor && trustedOwnShape
  && typeof descriptor.get === 'function'
  && descriptor.set === undefined
  && !Object.prototype.hasOwnProperty.call(descriptor, 'value')
  && descriptor.enumerable === trustedOwnShape.enumerable
  && descriptor.configurable === trustedOwnShape.configurable
);

export const viewportTrustedOwnDescriptorMatchesForTest = (descriptor, trustedOwnShape) => (
  WINDOW_LEVEL_TEST_CAPABILITIES_ENABLED
    ? trustedOwnDescriptorMatchesBoundary(descriptor, trustedOwnShape) : null
);

const readNativeTrustedEventType = (event, boundary) => {
  try {
    if (!boundary || !event || typeof event !== 'object') return false;
    // Reject own shadows, ducks, copies, and jsdom native-event proxies using
    // descriptors only, before any native getter can touch a Proxy `get` trap.
    if (Object.getOwnPropertyDescriptor(event, 'type')) return false;
    const ownTrusted = Object.getOwnPropertyDescriptor(event, 'isTrusted');
    if (boundary.readTrusted ? ownTrusted
      : !trustedOwnDescriptorMatchesBoundary(ownTrusted, boundary.trustedOwnShape)) return false;
    if (boundary.testRealmImplementationKey) {
      const implementationDescriptor = Object.getOwnPropertyDescriptor(
        event,
        boundary.testRealmImplementationKey,
      );
      const implementation = implementationDescriptor?.value;
      const wrapperDescriptor = implementation && Object.getOwnPropertyDescriptor(
        implementation,
        boundary.testRealmWrapperKey,
      );
      if (wrapperDescriptor?.value !== event) return false;
    }
    // Invoke the captured native Event accessor first. Ducks, copies, custom
    // prototypes, and proxies fail its internal receiver check without reading
    // attacker-controlled properties or proxy `get` traps.
    const type = Reflect.apply(boundary.readType, event, []);
    let isTrusted;
    if (boundary.readTrusted) {
      isTrusted = Reflect.apply(boundary.readTrusted, event, []);
    } else {
      isTrusted = Reflect.apply(ownTrusted.get, event, []);
    }
    return isTrusted === true && typeof type === 'string' ? type : false;
  } catch (_) {
    return false;
  }
};

const nativeTrustedMouseObservationKind = (event, boundary, allowedTypes) => {
  try {
    const type = readNativeTrustedEventType(event, boundary);
    if (!type || !allowedTypes.has(type)) return null;
    const prototype = Object.getPrototypeOf(event);
    const pointer = Boolean(boundary.PointerEventConstructor
      && prototype === boundary.pointerEventPrototype);
    const mouse = prototype === boundary.mouseEventPrototype;
    if (!pointer && !mouse) return null;
    if (!(event instanceof boundary.EventConstructor)
        || (pointer && !(event instanceof boundary.PointerEventConstructor))
        || (mouse && !(event instanceof boundary.MouseEventConstructor))) return null;

    if (Object.getOwnPropertyDescriptor(event, 'button')
        || Object.getOwnPropertyDescriptor(event, 'buttons')) return null;
    if (type.startsWith('pointer') ? !pointer : !mouse) return null;
    const button = Reflect.apply(
      pointer ? boundary.readPointerButton : boundary.readMouseButton,
      event,
      [],
    );
    const buttons = Reflect.apply(
      pointer ? boundary.readPointerButtons : boundary.readMouseButtons,
      event,
      [],
    );
    return primaryButtonLifecycleMatches(type, button, buttons)
      ? (pointer ? 'pointer' : 'mouse') : null;
  } catch (_) {
    return null;
  }
};

const nativeTrustedDomObservation = (event, boundary, allowedTypes) => Boolean(
  nativeTrustedMouseObservationKind(event, boundary, allowedTypes),
);

const nativeTrustedWheelObservation = (event, boundary) => {
  try {
    const type = readNativeTrustedEventType(event, boundary);
    return type === 'wheel'
      && Object.getPrototypeOf(event) === boundary.wheelEventPrototype
      && event instanceof boundary.EventConstructor
      && event instanceof boundary.WheelEventConstructor;
  } catch (_) {
    return false;
  }
};

const nativeTrustedZeroWheelObservation = (event, boundary) => {
  try {
    return nativeTrustedWheelObservation(event, boundary)
      && Reflect.apply(boundary.readWheelDeltaX, event, []) === 0
      && Reflect.apply(boundary.readWheelDeltaY, event, []) === 0
      && Reflect.apply(boundary.readWheelDeltaZ, event, []) === 0
      && Reflect.apply(boundary.readMouseCtrlKey, event, []) === false;
  } catch (_) {
    return false;
  }
};

const nativeTrustedRangeInputObservation = (event, boundary) => {
  try {
    const type = readNativeTrustedEventType(event, boundary);
    if (type !== 'input' && type !== 'change') return false;
    const prototype = Object.getPrototypeOf(event);
    if (prototype !== boundary.eventPrototype && prototype !== boundary.inputEventPrototype) return false;
    if (!(event instanceof boundary.EventConstructor)
        || (prototype === boundary.inputEventPrototype
          && !(event instanceof boundary.InputEventConstructor))
        || Object.getOwnPropertyDescriptor(event, 'target')) return false;
    const target = Reflect.apply(boundary.readTarget, event, []);
    if (!target || Object.getPrototypeOf(target) !== boundary.htmlInputElementPrototype
        || !(target instanceof boundary.HTMLInputElementConstructor)
        || Object.getOwnPropertyDescriptor(target, 'type')) return false;
    return Reflect.apply(boundary.readInputType, target, []) === 'range';
  } catch (_) {
    return false;
  }
};

const trustedDomObservation = (event, clock, allowedTypes) => {
  try {
    if (!event || typeof event !== 'object') return false;
    const clockState = WINDOW_LEVEL_CLOCK_STATES.get(clock);
    if (!clockState) return false;
    if (clockState.kind === 'test') {
      const testEvent = WINDOW_LEVEL_TEST_EVENT_STATES.get(event);
      return Boolean(testEvent && allowedTypes.has(testEvent.type)
        && primaryButtonLifecycleMatches(testEvent.type, testEvent.button, testEvent.buttons));
    }
    return clockState.kind === 'browser'
      && nativeTrustedDomObservation(event, clockState.browserBoundary, allowedTypes);
  } catch (_) {
    return false;
  }
};

const createWindowLevelClock = (readNow, kind, browserBoundary = null) => {
  if (typeof readNow !== 'function') throw new TypeError('window level clock reader is required');
  const clock = Object.freeze({});
  WINDOW_LEVEL_CLOCK_STATES.set(clock, { readNow, kind, browserBoundary });
  return clock;
};

export const createViewportWindowLevelBrowserClock = () => {
  const boundary = captureBrowserWindowLevelBoundary();
  return createWindowLevelClock(
    () => boundary ? Reflect.apply(boundary.readNow, boundary.performanceApi, []) : NaN,
    'browser',
    boundary,
  );
};

export const createViewportWindowLevelTrustedClockForTest = readNow => (
  WINDOW_LEVEL_TEST_CAPABILITIES_ENABLED ? createWindowLevelClock(readNow, 'test') : null
);

export const createViewportWindowLevelTrustedEventForTest = (type, button, buttons, wheel = {}) => {
  const resolvedButton = button === undefined ? (type === 'pointermove' ? -1 : 0) : button;
  const resolvedButtons = buttons === undefined
    ? (['pointerdown', 'mousedown', 'pointermove', 'mousemove'].includes(type) ? 1 : 0)
    : buttons;
  const deltaX = type === 'wheel' ? (wheel.deltaX ?? 0) : null;
  const deltaY = type === 'wheel' ? (wheel.deltaY ?? 0) : null;
  const deltaZ = type === 'wheel' ? (wheel.deltaZ ?? 0) : null;
  const ctrlKey = type === 'wheel' ? (wheel.ctrlKey ?? false) : null;
  if (!WINDOW_LEVEL_TEST_CAPABILITIES_ENABLED
      || typeof type !== 'string' || !Number.isSafeInteger(resolvedButton)
      || !Number.isSafeInteger(resolvedButtons) || resolvedButtons < 0
      || (type === 'wheel' && (![deltaX, deltaY, deltaZ].every(Number.isFinite)
        || typeof ctrlKey !== 'boolean'))) return null;
  const event = Object.freeze({});
  WINDOW_LEVEL_TEST_EVENT_STATES.set(event, Object.freeze({
    type,
    button: resolvedButton,
    buttons: resolvedButtons,
    deltaX,
    deltaY,
    deltaZ,
    ctrlKey,
  }));
  return event;
};

const readClockValue = clock => {
  let clockState = null;
  try { clockState = WINDOW_LEVEL_CLOCK_STATES.get(clock); } catch (_) { clockState = null; }
  if (!clockState) return null;
  let value;
  try { value = Reflect.apply(clockState.readNow, undefined, []); } catch (_) { return null; }
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
    && value <= Number.MAX_SAFE_INTEGER ? value : null;
};

const cineAuthorizationCurrent = authorization => {
  try {
    const authority = CINE_AUTHORIZATION_STATES.get(authorization);
    return Boolean(authority && authority.phaseState.active
      && installedCinePhase === authority.phaseState.capability
      && CINE_PHASE_STATES.get(authority.phaseState.capability) === authority.phaseState
      && installedSession === authority.session
      && REPORT_SESSION_STATES.get(authority.session) === authority.sessionState
      && !authority.sessionState.closed);
  } catch (_) {
    return false;
  }
};

const trustedCineInputObservation = (event, clock, interaction) => {
  try {
    const clockState = WINDOW_LEVEL_CLOCK_STATES.get(clock);
    if (!clockState || !['wheel', 'drag', 'slider'].includes(interaction)) return false;
    if (clockState.kind === 'test') {
      const observed = WINDOW_LEVEL_TEST_EVENT_STATES.get(event);
      if (!observed) return false;
      if (interaction === 'wheel') return observed.type === 'wheel' ? 'wheel' : null;
      if (interaction === 'drag') return ['pointermove', 'mousemove'].includes(observed.type)
        && primaryButtonLifecycleMatches(observed.type, observed.button, observed.buttons)
        ? (observed.type.startsWith('pointer') ? 'pointer' : 'mouse') : null;
      return observed.type === 'change' || observed.type === 'input' ? 'range' : null;
    }
    if (clockState.kind !== 'browser') return null;
    if (interaction === 'drag') {
      return nativeTrustedMouseObservationKind(
        event,
        clockState.browserBoundary,
        new Set(['pointermove', 'mousemove']),
      );
    }
    if (interaction === 'wheel') {
      return nativeTrustedWheelObservation(event, clockState.browserBoundary) ? 'wheel' : null;
    }
    return nativeTrustedRangeInputObservation(event, clockState.browserBoundary) ? 'range' : null;
  } catch (_) {
    return null;
  }
};

const trustedCineZeroWheelObservation = (event, clock) => {
  try {
    const clockState = WINDOW_LEVEL_CLOCK_STATES.get(clock);
    if (!clockState) return null;
    if (clockState.kind === 'test') {
      const observed = WINDOW_LEVEL_TEST_EVENT_STATES.get(event);
      return observed?.type === 'wheel' && observed.deltaX === 0 && observed.deltaY === 0
        && observed.deltaZ === 0 && observed.ctrlKey === false ? 'wheel' : null;
    }
    return clockState.kind === 'browser'
      && nativeTrustedZeroWheelObservation(event, clockState.browserBoundary) ? 'wheel' : null;
  } catch (_) {
    return null;
  }
};

const trustedCineBeginObservation = (event, clock, interaction) => {
  try {
    if (interaction !== 'drag') return event === null ? 'none' : null;
    const clockState = WINDOW_LEVEL_CLOCK_STATES.get(clock);
    if (!clockState) return null;
    if (clockState.kind === 'test') {
      const observed = WINDOW_LEVEL_TEST_EVENT_STATES.get(event);
      return observed && ['pointerdown', 'mousedown'].includes(observed.type)
        && primaryButtonLifecycleMatches(observed.type, observed.button, observed.buttons)
        ? (observed.type.startsWith('pointer') ? 'pointer' : 'mouse') : null;
    }
    return clockState.kind === 'browser'
      ? nativeTrustedMouseObservationKind(
        event,
        clockState.browserBoundary,
        new Set(['pointerdown', 'mousedown']),
      ) : null;
  } catch (_) {
    return null;
  }
};

const trustedCineReleaseObservation = (event, clock, interaction) => {
  try {
    const clockState = WINDOW_LEVEL_CLOCK_STATES.get(clock);
    if (!clockState || !['drag', 'slider'].includes(interaction)) return false;
    if (clockState.kind === 'test') {
      const observed = WINDOW_LEVEL_TEST_EVENT_STATES.get(event);
      if (!observed) return false;
      if (!['pointerup', 'mouseup'].includes(observed.type)
          || !primaryButtonLifecycleMatches(observed.type, observed.button, observed.buttons)) return null;
      return observed.type.startsWith('pointer') ? 'pointer' : 'mouse';
    }
    if (clockState.kind !== 'browser') return null;
    return nativeTrustedMouseObservationKind(
      event,
      clockState.browserBoundary,
      new Set(['pointerup', 'mouseup']),
    );
  } catch (_) {
    return null;
  }
};

const createDeterministicCineFrameScheduler = () => {
  const state = { callbacks: new Map() };
  const scheduler = Object.freeze({});
  CINE_TEST_FRAME_SCHEDULER_STATES.set(scheduler, state);
  return scheduler;
};

export const createViewportCineFrameSchedulerForTest = () => (
  WINDOW_LEVEL_TEST_CAPABILITIES_ENABLED ? createDeterministicCineFrameScheduler() : null
);

export const installViewportCineFrameSchedulerForTest = scheduler => {
  if (!WINDOW_LEVEL_TEST_CAPABILITIES_ENABLED) return false;
  let state = null;
  try { state = CINE_TEST_FRAME_SCHEDULER_STATES.get(scheduler); } catch (_) { state = null; }
  if (!state || (installedCineTestFrameScheduler
      && installedCineTestFrameScheduler !== scheduler)) return false;
  installedCineTestFrameScheduler = scheduler;
  return true;
};

const clearDeterministicCineFrameScheduler = scheduler => {
  let state = null;
  try { state = CINE_TEST_FRAME_SCHEDULER_STATES.get(scheduler); } catch (_) { state = null; }
  if (!state) return false;
  for (const request of state.callbacks.keys()) {
    const requestState = CINE_FRAME_REQUEST_STATES.get(request);
    if (requestState) requestState.active = false;
    CINE_FRAME_REQUEST_STATES.delete(request);
  }
  state.callbacks.clear();
  return true;
};

export const advanceViewportCineFrameSchedulerForTest = scheduler => {
  if (!WINDOW_LEVEL_TEST_CAPABILITIES_ENABLED
      || installedCineTestFrameScheduler !== scheduler) return false;
  let state = null;
  try { state = CINE_TEST_FRAME_SCHEDULER_STATES.get(scheduler); } catch (_) { state = null; }
  const next = state?.callbacks.entries().next();
  if (!state || next.done) return false;
  const [request, callback] = next.value;
  state.callbacks.delete(request);
  const requestState = CINE_FRAME_REQUEST_STATES.get(request);
  if (!requestState || requestState.schedulerState !== state || !requestState.active) return false;
  requestState.active = false;
  CINE_FRAME_REQUEST_STATES.delete(request);
  try { Reflect.apply(callback, undefined, []); } catch (_) {}
  return true;
};

export const snapshotViewportCineFrameSchedulerForTest = scheduler => {
  if (!WINDOW_LEVEL_TEST_CAPABILITIES_ENABLED) return null;
  let state = null;
  try { state = CINE_TEST_FRAME_SCHEDULER_STATES.get(scheduler); } catch (_) { state = null; }
  return state ? Object.freeze({ pendingFrameCount: state.callbacks.size }) : null;
};

export const uninstallViewportCineFrameSchedulerForTest = scheduler => {
  if (!WINDOW_LEVEL_TEST_CAPABILITIES_ENABLED || !installedCineTestFrameScheduler
      || scheduler !== installedCineTestFrameScheduler) return false;
  const current = installedCineTestFrameScheduler;
  installedCineTestFrameScheduler = null;
  clearDeterministicCineFrameScheduler(current);
  return true;
};

const requestCineFrame = callback => {
  if (typeof callback !== 'function') return null;
  const testState = installedCineTestFrameScheduler
    && CINE_TEST_FRAME_SCHEDULER_STATES.get(installedCineTestFrameScheduler);
  if (testState) {
    const request = Object.freeze({});
    CINE_FRAME_REQUEST_STATES.set(request, {
      active: true,
      kind: 'test',
      schedulerState: testState,
    });
    testState.callbacks.set(request, callback);
    return request;
  }
  if (!BROWSER_FRAME_SCHEDULER) return null;
  const request = Object.freeze({});
  const requestState = {
    active: true,
    kind: 'browser',
    nativeRequestId: null,
    synchronous: true,
  };
  CINE_FRAME_REQUEST_STATES.set(request, requestState);
  let nativeRequestId = null;
  try {
    nativeRequestId = BROWSER_FRAME_SCHEDULER.request(() => {
      if (requestState.synchronous || !requestState.active) {
        requestState.active = false;
        return;
      }
      requestState.active = false;
      CINE_FRAME_REQUEST_STATES.delete(request);
      Reflect.apply(callback, undefined, []);
    });
  } catch (_) {
    requestState.active = false;
  }
  requestState.synchronous = false;
  requestState.nativeRequestId = nativeRequestId;
  if (!requestState.active || nativeRequestId == null) {
    requestState.active = false;
    CINE_FRAME_REQUEST_STATES.delete(request);
    if (nativeRequestId != null) {
      try { BROWSER_FRAME_SCHEDULER.cancel(nativeRequestId); } catch (_) {}
    }
    return null;
  }
  return request;
};

const cancelCineFrame = request => {
  let requestState = null;
  try { requestState = CINE_FRAME_REQUEST_STATES.get(request); } catch (_) { requestState = null; }
  if (!requestState || !requestState.active) return false;
  requestState.active = false;
  CINE_FRAME_REQUEST_STATES.delete(request);
  if (requestState.kind === 'test') {
    return requestState.schedulerState.callbacks.delete(request);
  }
  if (requestState.kind !== 'browser' || !BROWSER_FRAME_SCHEDULER) return false;
  try {
    BROWSER_FRAME_SCHEDULER.cancel(requestState.nativeRequestId);
    return true;
  } catch (_) {
    return false;
  }
};

const viewportCineController = createViewportCineInteractionController({
  appendRetainedRow: (authorization, context, event) => {
    let authority = null;
    try { authority = CINE_AUTHORIZATION_STATES.get(authorization); } catch (_) { authority = null; }
    if (!authority || authority.sessionState.closed || authority.sessionState.draining) return null;
    return appendRetainedEvent(authority.sessionState, context, event);
  },
  cancelFrame: cancelCineFrame,
  clearTimer: timer => clearTimeout(timer),
  isAuthorizationCurrent: cineAuthorizationCurrent,
  observeTrustedBegin: trustedCineBeginObservation,
  observeTrustedInput: trustedCineInputObservation,
  observeTrustedRelease: trustedCineReleaseObservation,
  observeTrustedZeroWheel: trustedCineZeroWheelObservation,
  readClock: readClockValue,
  reserveOrdinal: reserveGlobalOrdinal,
  requestFrame: requestCineFrame,
  setTimer: (callback, delay) => setTimeout(callback, delay),
});

const CINE_BEGIN_KEYS = Object.freeze([
  'event', 'clock', 'phase', 'viewport', 'seriesGeneration', 'interaction', 'direction',
  'initialSlice', 'isRouteCurrent', 'isSeriesCurrent', 'isGenerationCurrent', 'isVisible',
  'isFocused', 'isActiveViewport',
]);
const CINE_PAINT_OBSERVATION_KEYS = Object.freeze(['onAuthenticatedPaint']);
const CINE_WHEEL_EFFECT_KINDS = Object.freeze([
  'focus-callback',
  'governor-begin-visible-interaction',
  'warmup-pause',
  'idle-preload-schedule',
  'speculative-authority-invalidation',
  'scroll-pending-mutation',
  'wheel-raf-schedule',
  'visible-load-start',
  'scrub-publication',
]);
const CINE_WHEEL_EFFECT_FIELDS = Object.freeze({
  'focus-callback': 'focusCallback',
  'governor-begin-visible-interaction': 'governorBeginVisibleInteraction',
  'warmup-pause': 'warmupPause',
  'idle-preload-schedule': 'idlePreloadSchedule',
  'speculative-authority-invalidation': 'speculativeAuthorityInvalidation',
  'scroll-pending-mutation': 'scrollPendingMutation',
  'wheel-raf-schedule': 'wheelRafSchedule',
  'visible-load-start': 'visibleLoadStart',
  'scrub-publication': 'scrubPublication',
});
const createCineWheelEffects = () => ({
  focusCallback: 0,
  governorBeginVisibleInteraction: 0,
  warmupPause: 0,
  idlePreloadSchedule: 0,
  speculativeAuthorityInvalidation: 0,
  scrollPendingMutation: 0,
  wheelRafSchedule: 0,
  visibleLoadStart: 0,
  scrubPublication: 0,
});
const freezeCineWheelEffects = effects => Object.freeze({
  focusCallback: effects.focusCallback,
  governorBeginVisibleInteraction: effects.governorBeginVisibleInteraction,
  warmupPause: effects.warmupPause,
  idlePreloadSchedule: effects.idlePreloadSchedule,
  speculativeAuthorityInvalidation: effects.speculativeAuthorityInvalidation,
  scrollPendingMutation: effects.scrollPendingMutation,
  wheelRafSchedule: effects.wheelRafSchedule,
  visibleLoadStart: effects.visibleLoadStart,
  scrubPublication: effects.scrubPublication,
});
const CINE_WHEEL_SLICE_STATE_KEYS = Object.freeze([
  'requestedSlice', 'acceptedSlice', 'renderedSlice', 'sourceOrdinal', 'pendingRenderOrdinal',
]);
const freezeCineWheelSliceState = value => {
  if (!value || typeof value !== 'object' || Array.isArray(value)
      || Object.getPrototypeOf(value) !== Object.prototype
      || !Object.isFrozen(value)
      || Reflect.ownKeys(value).length !== CINE_WHEEL_SLICE_STATE_KEYS.length
      || !CINE_WHEEL_SLICE_STATE_KEYS.every(key => Object.prototype.hasOwnProperty.call(value, key))) {
    return null;
  }
  const safeNullable = candidate => candidate === null
    || (Number.isSafeInteger(candidate) && candidate >= 0);
  if (!CINE_WHEEL_SLICE_STATE_KEYS.every(key => safeNullable(value[key]))) return null;
  return Object.freeze(Object.fromEntries(CINE_WHEEL_SLICE_STATE_KEYS.map(key => [key, value[key]])));
};

export const createViewportCineBrowserClock = createViewportWindowLevelBrowserClock;
export const createViewportCineTrustedClockForTest = createViewportWindowLevelTrustedClockForTest;
export const createViewportCineTrustedEventForTest = createViewportWindowLevelTrustedEventForTest;

export const beginViewportCineInteraction = options => {
  if (!isViewportEvidenceBenchmarkRequested()) return null;
  const snapshot = snapshotExactDataObject(options, CINE_BEGIN_KEYS);
  const session = installedSession;
  const phase = installedCinePhase;
  let sessionState = null;
  let phaseState = null;
  try { sessionState = session && REPORT_SESSION_STATES.get(session); } catch (_) { sessionState = null; }
  try { phaseState = phase && CINE_PHASE_STATES.get(phase); } catch (_) { phaseState = null; }
  if (!snapshot || !sessionState || sessionState.closed || !phaseState || !phaseState.active
      || phaseState.session !== session || phaseState.sessionState !== sessionState) return null;
  const interaction = viewportCineController.begin(Object.freeze({
    authorization: phaseState.authorization,
    originPhaseId: phaseState.originPhaseId,
    ...snapshot,
  }));
  if (interaction) {
    sessionState.cineInteractions.add(interaction);
    phaseState.cineInteractions.add(interaction);
    CINE_INTERACTION_PHASE_STATES.set(interaction, phaseState);
    CINE_DIAGNOSTIC_LEDGER_STATES.set(interaction, {
      phaseState,
      effects: createCineWheelEffects(),
      pendingNonzeroActivityOrdinal: null,
      retainedNonzero: null,
    });
  }
  return interaction;
};

export const observeViewportCineInput = (interaction, options) => (
  viewportCineController.observeInput(interaction, options)
);

// Private evidence-lifecycle bridge used only by the rendered viewport wheel
// coalescer. Valid activity advances quiet ownership without retaining a row.
export const observeViewportCineWheelActivity = (interaction, event) => (
  viewportCineController.observeWheelActivity(interaction, event)
);

const readCineDiagnosticAuthority = (interaction, viewport) => {
  let phaseState = null;
  let ledger = null;
  let controllerState = null;
  try { phaseState = CINE_INTERACTION_PHASE_STATES.get(interaction); } catch (_) { phaseState = null; }
  try { ledger = CINE_DIAGNOSTIC_LEDGER_STATES.get(interaction); } catch (_) { ledger = null; }
  try { controllerState = viewportCineController.interactionState(interaction); } catch (_) {
    controllerState = null;
  }
  if (!phaseState || !phaseState.active || phaseState.diagnostics?.readable !== true
      || installedCinePhase !== phaseState.capability || ledger?.phaseState !== phaseState
      || !controllerState || controllerState.viewport !== viewport
      || !Number.isSafeInteger(viewport) || viewport < 0
      || viewportCineController.isActive(interaction) !== true) return null;
  return { phaseState, ledger, controllerState };
};

const snapshotCineStructure = (phaseState, controllerState) => {
  const counts = {
    input: 0,
    command: 0,
    acceptedPublication: 0,
    paintConfirmation: 0,
    release: 0,
    settlement: 0,
    terminal: 0,
    phaseWork: 0,
    evidenceNetworkRequest: 0,
    decodedCacheConsumer: 0,
  };
  for (const row of phaseState.sessionState.events) {
    if (row?.originPhaseId !== phaseState.originPhaseId
        || row?.viewport !== controllerState.viewport) continue;
    const sameCine = row?.cineId === controllerState.cineId;
    if (sameCine && row.type === 'cine-input') counts.input += 1;
    else if (sameCine && row.type === 'cine-input-applied') counts.command += 1;
    else if (sameCine && row.type === 'cine-render-publication') counts.acceptedPublication += 1;
    else if (sameCine && row.type === 'cine-paint-confirmation') counts.paintConfirmation += 1;
    else if (sameCine && row.type === 'cine-release') counts.release += 1;
    else if (sameCine && row.type === 'cine-settlement') counts.settlement += 1;
    else if (sameCine && row.type === 'cine-terminal') counts.terminal += 1;
    if (sameCine && Number.isSafeInteger(row.phaseWorkId) && row.phaseWorkId > 0) counts.phaseWork += 1;
    if (row.phase === 'cine' && row.interaction === 'wheel'
        && row.type === 'network-request') counts.evidenceNetworkRequest += 1;
    if (row.phase === 'cine' && row.interaction === 'wheel'
        && row.type === 'decoded-cache-consumer-created') counts.decodedCacheConsumer += 1;
  }
  return Object.freeze(counts);
};

const cloneRetainedNonzero = value => (value ? Object.freeze({
  cineId: value.cineId,
  viewport: value.viewport,
  activityOrdinal: value.activityOrdinal,
  atMs: value.atMs,
}) : null);

const cloneZeroDelta = value => (value ? Object.freeze({
  cineId: value.cineId,
  viewport: value.viewport,
  activityOrdinal: value.activityOrdinal,
  atMs: value.atMs,
  priorNonzeroActivityOrdinal: value.priorNonzeroActivityOrdinal,
  priorNonzeroAtMs: value.priorNonzeroAtMs,
  pendingNonzeroActivityOrdinalBefore: value.pendingNonzeroActivityOrdinalBefore,
  pendingNonzeroActivityOrdinalAfter: value.pendingNonzeroActivityOrdinalAfter,
  latestNonzeroActivityOrdinalBefore: value.latestNonzeroActivityOrdinalBefore,
  latestNonzeroActivityOrdinalAfter: value.latestNonzeroActivityOrdinalAfter,
  sliceStateBefore: Object.freeze({ ...value.sliceStateBefore }),
  sliceStateAfter: Object.freeze({ ...value.sliceStateAfter }),
  effectsBefore: freezeCineWheelEffects(value.effectsBefore),
  effectsAfter: freezeCineWheelEffects(value.effectsAfter),
  structureBefore: Object.freeze({ ...value.structureBefore }),
  structureAfter: Object.freeze({ ...value.structureAfter }),
}) : null);

const snapshotCineDiagnostics = phaseState => {
  if (!phaseState?.active || phaseState.diagnostics?.readable !== true
      || installedCinePhase !== phaseState.capability) return null;
  return Object.freeze({
    originPhaseId: phaseState.originPhaseId,
    zeroDeltaCount: phaseState.diagnostics.zeroDeltaCount,
    lastAcceptedNonzero: cloneRetainedNonzero(phaseState.diagnostics.lastAcceptedNonzero),
    lastZeroDelta: cloneZeroDelta(phaseState.diagnostics.lastZeroDelta),
  });
};

export function observeViewportCineWheelActivityForFrame(interaction, event, viewport) {
  if (arguments.length !== 3) return null;
  const authority = readCineDiagnosticAuthority(interaction, viewport);
  if (!authority) return null;
  const controllerReceipt = viewportCineController.observeWheelActivityWithReceipt(interaction, event);
  const receiptState = viewportCineController.activityReceiptState(controllerReceipt);
  if (!controllerReceipt || !receiptState || receiptState.state !== authority.controllerState
      || !Number.isSafeInteger(receiptState.activityOrdinal) || receiptState.activityOrdinal <= 0
      || !Number.isFinite(receiptState.atMs) || receiptState.atMs < 0) return null;
  const receipt = Object.freeze({});
  const retained = Object.freeze({
    cineId: authority.controllerState.cineId,
    viewport,
    activityOrdinal: receiptState.activityOrdinal,
    atMs: receiptState.atMs,
  });
  authority.ledger.pendingNonzeroActivityOrdinal = receiptState.activityOrdinal;
  authority.ledger.retainedNonzero = retained;
  if (authority.phaseState.diagnostics.zeroDeltaCount === 0) {
    authority.phaseState.diagnostics.lastAcceptedNonzero = retained;
  }
  CINE_ACTIVITY_RECEIPT_STATES.set(receipt, {
    controllerReceipt,
    interaction,
    phaseState: authority.phaseState,
    ledger: authority.ledger,
    activityOrdinal: receiptState.activityOrdinal,
    pending: true,
  });
  return receipt;
}

export function clearViewportCineWheelActivityForFrame(receipt) {
  if (arguments.length !== 1) return false;
  let receiptState = null;
  try { receiptState = CINE_ACTIVITY_RECEIPT_STATES.get(receipt); } catch (_) { receiptState = null; }
  if (!receiptState || receiptState.pending !== true || !receiptState.phaseState.active
      || receiptState.ledger.pendingNonzeroActivityOrdinal !== receiptState.activityOrdinal
      || viewportCineController.clearActivityReceipt(receiptState.controllerReceipt) !== true) return false;
  receiptState.pending = false;
  receiptState.ledger.pendingNonzeroActivityOrdinal = null;
  return true;
}

export function recordViewportCineWheelEffect(interaction, viewport, kind) {
  if (arguments.length !== 3 || !CINE_WHEEL_EFFECT_KINDS.includes(kind)) return false;
  const authority = readCineDiagnosticAuthority(interaction, viewport);
  const field = CINE_WHEEL_EFFECT_FIELDS[kind];
  if (!authority || !field) return false;
  const current = authority.ledger.effects[field];
  if (!Number.isSafeInteger(current) || current < 0 || current === Number.MAX_SAFE_INTEGER) {
    viewportCineController.closeInteraction(interaction, 'overflow');
    return false;
  }
  authority.ledger.effects[field] = current + 1;
  return true;
}

export function reserveViewportCinePendingRenderOrdinal(interaction, viewport) {
  if (arguments.length !== 2) return null;
  const authority = readCineDiagnosticAuthority(interaction, viewport);
  if (!authority) return null;
  try { return reserveGlobalOrdinal('cinePendingRender'); } catch (_) {
    viewportCineController.closeInteraction(interaction, 'overflow');
    return null;
  }
}

export function observeViewportCineZeroDelta(interaction, event, viewport, readSliceState) {
  if (arguments.length !== 4 || typeof readSliceState !== 'function') return false;
  const authority = readCineDiagnosticAuthority(interaction, viewport);
  const prior = authority?.ledger.retainedNonzero;
  if (!authority || !prior || prior !== authority.phaseState.diagnostics.lastAcceptedNonzero) return false;
  let sliceStateBefore = null;
  try { sliceStateBefore = freezeCineWheelSliceState(readSliceState()); } catch (_) {
    sliceStateBefore = null;
  }
  if (!sliceStateBefore) return false;
  const effectsBefore = freezeCineWheelEffects(authority.ledger.effects);
  const structureBefore = snapshotCineStructure(authority.phaseState, authority.controllerState);
  const pendingBefore = authority.ledger.pendingNonzeroActivityOrdinal;
  const controllerReceipt = viewportCineController.observeZeroWheelActivityWithReceipt(interaction, event);
  const receiptState = viewportCineController.activityReceiptState(controllerReceipt);
  if (!controllerReceipt || !receiptState || receiptState.state !== authority.controllerState
      || !Number.isSafeInteger(receiptState.activityOrdinal)
      || receiptState.activityOrdinal <= prior.activityOrdinal
      || !Number.isFinite(receiptState.atMs) || receiptState.atMs < prior.atMs) return false;
  viewportCineController.clearActivityReceipt(controllerReceipt);
  let sliceStateAfter = null;
  try { sliceStateAfter = freezeCineWheelSliceState(readSliceState()); } catch (_) {
    sliceStateAfter = null;
  }
  if (!sliceStateAfter) {
    viewportCineController.closeInteraction(interaction, 'failed');
    return false;
  }
  const zeroDeltaCount = authority.phaseState.diagnostics.zeroDeltaCount;
  if (!Number.isSafeInteger(zeroDeltaCount) || zeroDeltaCount < 0
      || zeroDeltaCount === Number.MAX_SAFE_INTEGER) {
    viewportCineController.closeInteraction(interaction, 'overflow');
    return false;
  }
  const row = Object.freeze({
    cineId: authority.controllerState.cineId,
    viewport,
    activityOrdinal: receiptState.activityOrdinal,
    atMs: receiptState.atMs,
    priorNonzeroActivityOrdinal: prior.activityOrdinal,
    priorNonzeroAtMs: prior.atMs,
    pendingNonzeroActivityOrdinalBefore: pendingBefore,
    pendingNonzeroActivityOrdinalAfter: authority.ledger.pendingNonzeroActivityOrdinal,
    latestNonzeroActivityOrdinalBefore: prior.activityOrdinal,
    latestNonzeroActivityOrdinalAfter: authority.ledger.retainedNonzero.activityOrdinal,
    sliceStateBefore,
    sliceStateAfter,
    effectsBefore,
    effectsAfter: freezeCineWheelEffects(authority.ledger.effects),
    structureBefore,
    structureAfter: snapshotCineStructure(authority.phaseState, authority.controllerState),
  });
  authority.phaseState.diagnostics.zeroDeltaCount = zeroDeltaCount + 1;
  authority.phaseState.diagnostics.lastZeroDelta = row;
  return true;
}

export const consumeViewportCineInputForCoordinator = (input, targetSlice) => (
  viewportCineController.consumeInput(input, targetSlice)
);

export const createViewportCineRenderBinding = command => (
  viewportCineController.createRenderBinding(command)
);

export const releaseViewportCineInteraction = (interaction, options) => (
  viewportCineController.release(interaction, options)
);

export const settleViewportCineInteraction = interaction => (
  viewportCineController.settle(interaction)
);

const terminalizeViewportCineInteraction = (interaction, outcome) => {
  const changed = viewportCineController.closeInteraction(interaction, outcome);
  if (!changed) return false;
  let phaseState = null;
  try { phaseState = CINE_INTERACTION_PHASE_STATES.get(interaction); } catch (_) { phaseState = null; }
  phaseState?.cineInteractions.delete(interaction);
  phaseState?.sessionState?.cineInteractions.delete(interaction);
  return true;
};

export const cancelViewportCineInteraction = interaction => (
  terminalizeViewportCineInteraction(interaction, 'cancelled')
);

export const failViewportCineInteraction = interaction => (
  terminalizeViewportCineInteraction(interaction, 'failed')
);

export const isViewportCineInteractionActive = interaction => (
  viewportCineController.isActive(interaction)
);

export const snapshotViewportCineInteraction = interaction => (
  viewportCineController.snapshot(interaction)
);

const windowLevelBase = state => ({
  phase: state.phase,
  viewport: state.viewport,
  generation: state.generation,
  interactionOrdinal: state.interactionOrdinal,
});

const appendWindowLevelEvent = (state, event) => appendEvent(
  state.sessionState,
  Object.freeze(windowLevelBase(state)),
  Object.freeze(event),
);

const windowLevelStateIsCurrent = state => Boolean(state && state.active
  && installedSession === state.session && !state.sessionState.closed
  && REPORT_SESSION_STATES.get(state.session) === state.sessionState
  && state.sessionState.windowLevelInteractions.has(state.interaction));

const readInteractionClock = state => {
  const atMs = readClockValue(state.clock);
  if (atMs === null || atMs < state.lastAtMs) return null;
  state.lastAtMs = atMs;
  return atMs;
};

const windowLevelAuthorityCurrent = state => {
  if (!windowLevelStateIsCurrent(state)) return false;
  let current = false;
  try { current = state.isAuthorityCurrent() === true; } catch (_) { current = false; }
  return current && windowLevelStateIsCurrent(state);
};

const terminalizeWindowLevelState = (state, outcome, atMs = state?.lastAtMs) => {
  if (!state?.active || !['settled', 'cancelled', 'failed'].includes(outcome)) return false;
  const finalWindowLevel = state.currentInput?.windowLevel || state.startWindowLevel;
  const terminal = {
    type: 'window-level-terminal',
    outcome,
    atMs: Number.isFinite(atMs) && atMs >= state.lastAtMs ? atMs : state.lastAtMs,
    ww: finalWindowLevel.ww,
    wc: finalWindowLevel.wc,
  };
  if (outcome === 'settled') {
    terminal.inputOrdinal = state.currentInput.inputOrdinal;
    terminal.evidenceOrdinal = state.currentInput.evidenceOrdinal;
  }
  appendWindowLevelEvent(state, terminal);
  state.active = false;
  state.terminalOutcome = outcome;
  state.sessionState.windowLevelInteractions.delete(state.interaction);
  return true;
};

const failWindowLevelState = state => terminalizeWindowLevelState(state, 'failed');

export const beginViewportWindowLevelInteraction = options => {
  const snapshot = snapshotExactDataObject(options, WINDOW_LEVEL_START_KEYS);
  if (!snapshot || !isViewportEvidenceBenchmarkRequested()) return null;
  let sessionState = null;
  const session = installedSession;
  try { sessionState = session && REPORT_SESSION_STATES.get(session); } catch (_) { sessionState = null; }
  if (!sessionState || sessionState.closed) return null;
  const atMs = readClockValue(snapshot.clock);
  if (atMs === null || installedSession !== session || sessionState.closed
      || !trustedDomObservation(snapshot.event, snapshot.clock, new Set(['pointerdown', 'mousedown']))) return null;
  const startWindowLevel = snapshotWindowLevel(snapshot.windowLevel);
  if (!startWindowLevel || !WINDOW_LEVEL_PHASES.has(snapshot.phase)
      || !Number.isSafeInteger(snapshot.viewport) || snapshot.viewport < 0
      || !Number.isSafeInteger(snapshot.generation) || snapshot.generation <= 0
      || typeof snapshot.isAuthorityCurrent !== 'function') return null;
  let authorityCurrent = false;
  try { authorityCurrent = snapshot.isAuthorityCurrent() === true; } catch (_) { authorityCurrent = false; }
  if (!authorityCurrent || installedSession !== session || sessionState.closed) return null;
  let interactionOrdinal;
  try { interactionOrdinal = reserveGlobalOrdinal('windowLevelInteraction'); } catch (_) { return null; }
  if (sessionState.allocatedWindowLevelInteractionOrdinals.has(interactionOrdinal)) return null;
  sessionState.allocatedWindowLevelInteractionOrdinals.add(interactionOrdinal);
  const interaction = Object.freeze({});
  const state = {
    interaction,
    session,
    sessionState,
    clock: snapshot.clock,
    isAuthorityCurrent: snapshot.isAuthorityCurrent,
    phase: snapshot.phase,
    viewport: snapshot.viewport,
    generation: snapshot.generation,
    interactionOrdinal,
    startWindowLevel,
    lastAtMs: atMs,
    currentInput: null,
    released: null,
    active: true,
    terminalOutcome: null,
  };
  WINDOW_LEVEL_INTERACTION_STATES.set(interaction, state);
  sessionState.windowLevelInteractions.add(interaction);
  appendWindowLevelEvent(state, {
    type: 'window-level-trusted-start',
    atMs,
    ww: startWindowLevel.ww,
    wc: startWindowLevel.wc,
  });
  return interaction;
};

export const observeViewportWindowLevelInput = (interaction, options) => {
  let state = null;
  try { state = WINDOW_LEVEL_INTERACTION_STATES.get(interaction); } catch (_) { state = null; }
  if (!windowLevelStateIsCurrent(state) || state.released) return null;
  const atMs = readInteractionClock(state);
  const snapshot = snapshotExactDataObject(options, WINDOW_LEVEL_INPUT_KEYS);
  if (atMs === null || !snapshot
      || !trustedDomObservation(snapshot.event, state.clock, new Set(['pointermove', 'mousemove']))) {
    failWindowLevelState(state);
    return null;
  }
  const windowLevel = snapshotWindowLevel(snapshot.windowLevel);
  if (!windowLevel || !windowLevelAuthorityCurrent(state)) {
    failWindowLevelState(state);
    return null;
  }
  let inputOrdinal;
  try { inputOrdinal = reserveGlobalOrdinal('windowLevelInput'); } catch (_) {
    failWindowLevelState(state);
    return null;
  }
  if (state.sessionState.allocatedWindowLevelInputOrdinals.has(inputOrdinal)) {
    failWindowLevelState(state);
    return null;
  }
  state.sessionState.allocatedWindowLevelInputOrdinals.add(inputOrdinal);
  const input = Object.freeze({});
  const inputState = {
    input,
    interactionState: state,
    inputOrdinal,
    atMs,
    windowLevel,
    status: 'pending',
    publication: null,
    evidenceOrdinal: null,
    paintAccepted: false,
  };
  const previous = state.currentInput;
  if (previous && previous.status !== 'coalesced') {
    previous.status = 'coalesced';
    appendWindowLevelEvent(state, {
      type: 'window-level-input-coalesced',
      inputOrdinal: previous.inputOrdinal,
      supersededByInputOrdinal: inputOrdinal,
      atMs,
      ww: previous.windowLevel.ww,
      wc: previous.windowLevel.wc,
    });
  }
  state.currentInput = inputState;
  WINDOW_LEVEL_INPUT_STATES.set(input, inputState);
  appendWindowLevelEvent(state, {
    type: 'window-level-input',
    inputOrdinal,
    atMs,
    ww: windowLevel.ww,
    wc: windowLevel.wc,
  });
  return input;
};

export const consumeViewportWindowLevelInputForCoordinator = (input, windowLevel) => {
  let inputState = null;
  try { inputState = WINDOW_LEVEL_INPUT_STATES.get(input); } catch (_) { inputState = null; }
  if (!inputState) return false;
  const state = inputState.interactionState;
  const appliedWindowLevel = snapshotWindowLevel(windowLevel);
  if (!windowLevelStateIsCurrent(state) || state.currentInput !== inputState
      || inputState.status !== 'pending' || !sameWindowLevel(appliedWindowLevel, inputState.windowLevel)) {
    failWindowLevelState(state);
    return false;
  }
  const atMs = readInteractionClock(state);
  if (atMs === null || !windowLevelAuthorityCurrent(state)) {
    failWindowLevelState(state);
    return false;
  }
  inputState.status = 'applied';
  appendWindowLevelEvent(state, {
    type: 'window-level-input-applied',
    inputOrdinal: inputState.inputOrdinal,
    atMs,
    ww: inputState.windowLevel.ww,
    wc: inputState.windowLevel.wc,
  });
  return true;
};

const resolveWindowLevelRenderBinding = ({ context, binding, sessionState }) => {
  if (context?.phase === 'cine') {
    let phaseState = null;
    try {
      phaseState = installedCinePhase && CINE_PHASE_STATES.get(installedCinePhase);
    } catch (_) {
      phaseState = null;
    }
    if (!phaseState || !phaseState.active || phaseState.sessionState !== sessionState) return null;
    const cineCommandState = viewportCineController.prepareRenderBinding(
      binding,
      context,
      phaseState.authorization,
    );
    return cineCommandState
      ? Object.freeze({ inputState: null, windowLevel: null, cineCommandState })
      : null;
  }
  if (context?.interaction !== 'drag') return binding == null
    ? Object.freeze({ inputState: null, windowLevel: null, cineCommandState: null }) : null;
  const snapshot = snapshotExactDataObject(binding, WINDOW_LEVEL_RENDER_BINDING_KEYS);
  if (!snapshot) return null;
  let inputState = null;
  try { inputState = WINDOW_LEVEL_INPUT_STATES.get(snapshot.input); } catch (_) { inputState = null; }
  const windowLevel = snapshotWindowLevel(snapshot.windowLevel);
  const interactionState = inputState?.interactionState;
  if (!inputState || !windowLevel || !windowLevelStateIsCurrent(interactionState)
      || interactionState.sessionState !== sessionState
      || interactionState.currentInput !== inputState || inputState.status !== 'applied'
      || interactionState.phase !== context.phase
      || interactionState.viewport !== context.viewport
      || interactionState.generation !== context.generation
      || !sameWindowLevel(windowLevel, inputState.windowLevel)
      || !windowLevelAuthorityCurrent(interactionState)) return null;
  return Object.freeze({ inputState, windowLevel, cineCommandState: null });
};

const renderEvidenceMatchesWindowLevelInput = (inputState, renderState) => {
  const state = inputState.interactionState;
  return Boolean(renderState && renderState.sessionState === state.sessionState
    && renderState.windowLevelInputState === inputState
    && sameWindowLevel(renderState.renderWindowLevel, inputState.windowLevel)
    && renderState.context?.phase === state.phase
    && renderState.context?.viewport === state.viewport
    && renderState.context?.generation === state.generation
    && renderState.context?.interaction === 'drag'
    && !renderState.cancelled && !renderState.finished
    && (renderState.published || renderState.publishing)
    && renderState.acceptedSurfaces.size > 0);
};

const renderOperationModuleStateIsAuthoritative = operationState => {
  try {
    const authority = operationState?.renderAuthority;
    return Boolean(authority && operationState.operation
      && operationState.session === authority.session
      && operationState.sessionState === authority.sessionState
      && operationState.context === authority.context
      && installedSession === authority.session
      && REPORT_SESSION_STATES.get(authority.session) === authority.sessionState
      && !authority.sessionState.closed
      && OPERATION_STATES.get(operationState.operation) === operationState
      && authority.sessionState.operations.has(operationState.operation));
  } catch (_) {
    return false;
  }
};

const revalidateRenderOperationAuthority = operationState => {
  if (!renderOperationModuleStateIsAuthoritative(operationState)) return false;
  const callbacks = operationState.renderAuthority.callbacks;
  if (!Array.isArray(callbacks) || callbacks.length !== 5) return false;
  for (const callback of callbacks) {
    if (!renderOperationModuleStateIsAuthoritative(operationState)) return false;
    let current = false;
    try { current = Reflect.apply(callback, undefined, []) === true; } catch (_) { current = false; }
    if (!current || !renderOperationModuleStateIsAuthoritative(operationState)) return false;
  }
  return renderOperationModuleStateIsAuthoritative(operationState);
};

const currentRenderOperationSource = operationState => {
  try {
    if (!operationState || operationState.cancelled || operationState.finished
        || operationState.frameReleased) return null;
    if (operationState.cacheRootState) {
      const rootState = operationState.cacheRootState;
      return rootState.sessionState === operationState.sessionState
        && rootState.consumers.has(operationState.operation)
        && !rootState.frameReleased ? rootState.image : null;
    }
    return operationState.decodedImage || null;
  } catch (_) {
    return null;
  }
};

const cineRenderOperationIsAuthoritative = (operationState, operation) => Boolean(
  operationState?.cineCommandState
  && operationState.operation === operation
  && operationState.context?.phase === 'cine'
  && operationState.published === true
  && operationState.publishing === false
  && operationState.acceptedSurfaces.size === 1
  && currentRenderOperationSource(operationState)
  && revalidateRenderOperationAuthority(operationState),
);

const claimAcceptedVisibleSurface = (operationState, surface) => {
  if (operationState.acceptedSurfaces.has(surface)) return null;
  if (operationState.cineCommandState) {
    if (operationState.acceptedSurfaces.size === 0) {
      operationState.acceptedSurfaces.add(surface);
      return 'accepted';
    }
    const mayHandoffPreview = operationState.acceptedSurfaces.size === 1
      && operationState.acceptedSurfaces.has('preview')
      && surface === 'active-image'
      && !operationState.cineCommandState.publication;
    if (!mayHandoffPreview) return null;
    operationState.acceptedSurfaces.delete('preview');
    operationState.acceptedSurfaces.add('active-image');
    return 'handoff';
  }
  operationState.acceptedSurfaces.add(surface);
  return 'accepted';
};

export const acceptViewportCineRenderPublication = (command, operation, requestGeneration) => {
  let operationState = null;
  try { operationState = OPERATION_STATES.get(operation); } catch (_) { operationState = null; }
  if (!cineRenderOperationIsAuthoritative(operationState, operation)) return null;
  const source = currentRenderOperationSource(operationState);
  const surface = [...operationState.acceptedSurfaces][0];
  return viewportCineController.acceptRenderPublication(
    command,
    operation,
    Object.freeze({
      source,
      generation: operationState.context.generation,
      requestGeneration,
      slice: operationState.context.slice,
      surface,
    }),
    () => cineRenderOperationIsAuthoritative(operationState, operation)
      && currentRenderOperationSource(operationState) === source,
  );
};

export const observeViewportCinePaint = function observeViewportCinePaint(
  publication,
  operation,
  exactOptions,
) {
  if (arguments.length !== 3) return false;
  const emptyOptions = snapshotExactDataObject(exactOptions, []);
  const callbackOptions = snapshotExactDataObject(exactOptions, CINE_PAINT_OBSERVATION_KEYS);
  if (!emptyOptions && (!callbackOptions
      || typeof callbackOptions.onAuthenticatedPaint !== 'function')) return false;
  let operationState = null;
  try { operationState = OPERATION_STATES.get(operation); } catch (_) { operationState = null; }
  if (!cineRenderOperationIsAuthoritative(operationState, operation)) return false;
  const source = currentRenderOperationSource(operationState);
  return viewportCineController.requestPaintObservation(
    publication,
    operation,
    Object.freeze({ source, generation: operationState.context.generation }),
    () => cineRenderOperationIsAuthoritative(operationState, operation)
      && operationState.context.generation === operationState.cineCommandState?.inputState?.state?.seriesGeneration
      && currentRenderOperationSource(operationState) === source,
    paint => callbackOptions ? callbackOptions.onAuthenticatedPaint(paint) === true : true,
  );
};

export const acceptViewportWindowLevelRenderPublication = (input, renderEvidence) => {
  let inputState = null;
  let renderState = null;
  try {
    inputState = WINDOW_LEVEL_INPUT_STATES.get(input);
    renderState = OPERATION_STATES.get(renderEvidence);
  } catch (_) {
    return null;
  }
  if (!inputState) return null;
  const state = inputState.interactionState;
  if (!windowLevelStateIsCurrent(state) || state.currentInput !== inputState
      || inputState.status !== 'applied' || inputState.publication
      || !renderEvidenceMatchesWindowLevelInput(inputState, renderState)) {
    failWindowLevelState(state);
    return null;
  }
  const atMs = readInteractionClock(state);
  if (atMs === null || !windowLevelAuthorityCurrent(state)
      || !renderEvidenceMatchesWindowLevelInput(inputState, renderState)
      || !revalidateRenderOperationAuthority(renderState)) {
    failWindowLevelState(state);
    return null;
  }
  const publication = Object.freeze({});
  const publicationState = { publication, inputState, renderEvidence, renderState, atMs, painted: false };
  inputState.publication = publicationState;
  inputState.evidenceOrdinal = renderState.evidenceOrdinal;
  WINDOW_LEVEL_PUBLICATION_STATES.set(publication, publicationState);
  appendWindowLevelEvent(state, {
    type: 'window-level-render-publication',
    inputOrdinal: inputState.inputOrdinal,
    evidenceOrdinal: renderState.evidenceOrdinal,
    atMs,
    ww: inputState.windowLevel.ww,
    wc: inputState.windowLevel.wc,
  });
  return publication;
};

export const acceptViewportWindowLevelPaint = (input, publication, renderEvidence) => {
  let inputState = null;
  let publicationState = null;
  let renderState = null;
  try {
    inputState = WINDOW_LEVEL_INPUT_STATES.get(input);
    publicationState = WINDOW_LEVEL_PUBLICATION_STATES.get(publication);
    renderState = OPERATION_STATES.get(renderEvidence);
  } catch (_) {
    return false;
  }
  if (!inputState) return false;
  const state = inputState.interactionState;
  if (!publicationState || publicationState.inputState !== inputState
      || publicationState.renderEvidence !== renderEvidence
      || publicationState.renderState !== renderState
      || !windowLevelStateIsCurrent(state) || state.currentInput !== inputState
      || inputState.publication !== publicationState || publicationState.painted
      || !renderEvidenceMatchesWindowLevelInput(inputState, renderState)) {
    failWindowLevelState(state);
    return false;
  }
  const atMs = readInteractionClock(state);
  if (atMs === null || !windowLevelAuthorityCurrent(state)
      || !renderEvidenceMatchesWindowLevelInput(inputState, renderState)
      || !revalidateRenderOperationAuthority(renderState)) {
    failWindowLevelState(state);
    return false;
  }
  publicationState.painted = true;
  inputState.paintAccepted = true;
  appendWindowLevelEvent(state, {
    type: 'window-level-paint',
    inputOrdinal: inputState.inputOrdinal,
    evidenceOrdinal: renderState.evidenceOrdinal,
    atMs,
    ww: inputState.windowLevel.ww,
    wc: inputState.windowLevel.wc,
  });
  return true;
};

export const releaseViewportWindowLevelInteraction = (interaction, options) => {
  let state = null;
  try { state = WINDOW_LEVEL_INTERACTION_STATES.get(interaction); } catch (_) { state = null; }
  if (!windowLevelStateIsCurrent(state)) return false;
  if (state.released) {
    failWindowLevelState(state);
    return false;
  }
  const atMs = readInteractionClock(state);
  const snapshot = snapshotExactDataObject(options, WINDOW_LEVEL_INPUT_KEYS);
  if (atMs === null || !snapshot
      || !trustedDomObservation(snapshot.event, state.clock, new Set(['pointerup', 'mouseup']))) {
    failWindowLevelState(state);
    return false;
  }
  const windowLevel = snapshotWindowLevel(snapshot.windowLevel);
  if (!windowLevel || !windowLevelAuthorityCurrent(state)) {
    failWindowLevelState(state);
    return false;
  }
  const currentInput = state.currentInput;
  state.released = { atMs, windowLevel, inputOrdinal: currentInput?.inputOrdinal || null };
  appendWindowLevelEvent(state, {
    type: 'window-level-release',
    ...(currentInput ? { inputOrdinal: currentInput.inputOrdinal } : {}),
    atMs,
    ww: windowLevel.ww,
    wc: windowLevel.wc,
  });
  if (!currentInput || !sameWindowLevel(windowLevel, currentInput.windowLevel)) {
    failWindowLevelState(state);
    return false;
  }
  return true;
};

export const settleViewportWindowLevelInteraction = interaction => {
  let state = null;
  try { state = WINDOW_LEVEL_INTERACTION_STATES.get(interaction); } catch (_) { state = null; }
  if (!windowLevelStateIsCurrent(state)) return false;
  const inputState = state.currentInput;
  if (!state.released || !inputState || state.released.inputOrdinal !== inputState.inputOrdinal
      || inputState.status !== 'applied' || !inputState.publication
      || !inputState.paintAccepted || !sameWindowLevel(state.released.windowLevel, inputState.windowLevel)) return false;
  const atMs = readInteractionClock(state);
  if (atMs === null || !windowLevelAuthorityCurrent(state)) {
    failWindowLevelState(state);
    return false;
  }
  return terminalizeWindowLevelState(state, 'settled', atMs);
};

export const cancelViewportWindowLevelInteraction = interaction => {
  let state = null;
  try { state = WINDOW_LEVEL_INTERACTION_STATES.get(interaction); } catch (_) { state = null; }
  if (!windowLevelStateIsCurrent(state)) return false;
  const atMs = readInteractionClock(state);
  return terminalizeWindowLevelState(state, 'cancelled', atMs === null ? state.lastAtMs : atMs);
};

export const failViewportWindowLevelInteraction = interaction => {
  let state = null;
  try { state = WINDOW_LEVEL_INTERACTION_STATES.get(interaction); } catch (_) { state = null; }
  if (!windowLevelStateIsCurrent(state)) return false;
  const atMs = readInteractionClock(state);
  return terminalizeWindowLevelState(state, 'failed', atMs === null ? state.lastAtMs : atMs);
};

const closeWindowLevelInteractions = state => {
  [...state.windowLevelInteractions].forEach(interaction => {
    const interactionState = WINDOW_LEVEL_INTERACTION_STATES.get(interaction);
    if (interactionState?.active) terminalizeWindowLevelState(interactionState, 'cancelled');
  });
};

const sealCinePhaseDiagnostics = phaseState => {
  if (!phaseState?.active || phaseState.diagnostics?.readable !== true
      || phaseState.diagnostics.sealed || phaseState.sessionState.sealedCineDiagnostics) return false;
  const sealed = snapshotCineDiagnostics(phaseState);
  if (!sealed) return false;
  phaseState.diagnostics.sealed = sealed;
  phaseState.diagnostics.readable = false;
  phaseState.sessionState.sealedCineDiagnostics = sealed;
  return true;
};

const closeCinePhaseState = (phaseState, reason) => {
  if (!phaseState?.active || !['cancelled', 'replaced'].includes(reason)) return false;
  [...phaseState.cineInteractions].forEach(interaction => {
    viewportCineController.closeInteraction(interaction, reason);
    phaseState.sessionState.cineInteractions.delete(interaction);
  });
  phaseState.cineInteractions.clear();
  phaseState.active = false;
  if (installedCinePhase === phaseState.capability) installedCinePhase = null;
  return true;
};

const closeSessionState = (state, cineReason = 'cancelled') => {
  const changed = !state.closed || state.operations.size > 0 || state.roots.size > 0
    || state.pendingCleanup.size > 0 || state.windowLevelInteractions.size > 0
    || state.cineInteractions.size > 0 || state.cinePhases.size > 0;
  [...state.cinePhases].forEach(phaseState => closeCinePhaseState(phaseState, cineReason));
  state.cinePhases.clear();
  [...state.cineInteractions].forEach(interaction => {
    viewportCineController.closeInteraction(interaction, cineReason);
  });
  state.cineInteractions.clear();
  state.sealedCineDiagnostics = null;
  state.closed = true;
  closeWindowLevelInteractions(state);
  // Retry only cleanup retained from a prior boundary. A revocation failure
  // created while closing current consumers remains truthfully pending until a
  // later close call rather than being hidden by an immediate same-call retry.
  retryPendingCleanup(state);
  [...state.operations].forEach(operation => {
    const operationState = OPERATION_STATES.get(operation);
    if (operationState) cancelOperation(operationState);
  });
  [...state.roots].forEach(root => evictCacheRootState(CACHE_ROOT_STATES.get(root)));
  return changed;
};

export const createViewportEvidenceReportSession = () => {
  const state = {
    closed: false,
    draining: false,
    operations: new Set(),
    roots: new Set(),
    windowLevelInteractions: new Set(),
    cineInteractions: new Set(),
    cinePhases: new Set(),
    cineAuthorization: null,
    cineDrainIssued: false,
    sealedCineDiagnostics: null,
    allocatedCacheRootOrdinals: new Set(),
    allocatedConsumerOrdinals: new Set(),
    allocatedWindowLevelInteractionOrdinals: new Set(),
    allocatedWindowLevelInputOrdinals: new Set(),
    pendingCleanup: new Set(),
    events: [],
    nextSequence: 1,
    requestAllocator: createViewportRequestOrdinalAllocatorAdapter(() => reserveGlobalOrdinal('request')),
  };
  const capability = Object.freeze({
    snapshot: () => Object.freeze({
      closed: state.closed,
      activeOperations: state.operations.size,
      activeCacheRoots: state.roots.size,
      pendingCleanup: state.pendingCleanup.size,
      retainedEvents: state.events.length,
      lastSequence: state.nextSequence - 1,
    }),
    drain: () => {
      state.draining = true;
      try {
        const drained = Object.freeze([...state.events]);
        RETAINED_EVENT_DRAINS.add(drained);
        const containsCine = state.events.some(row => typeof row.type === 'string'
          && row.type.startsWith('cine-'));
        if (!containsCine || !state.cineDrainIssued) {
          viewportCineController.brandDrain(drained);
          if (containsCine) state.cineDrainIssued = true;
        }
        state.events.length = 0;
        return drained;
      } finally {
        state.draining = false;
      }
    },
    close: () => closeSessionState(state),
  });
  REPORT_SESSION_STATES.set(capability, state);
  const cineAuthorization = Object.freeze({});
  state.cineAuthorization = cineAuthorization;
  CINE_AUTHORIZATION_STATES.set(cineAuthorization, Object.freeze({
    session: capability,
    sessionState: state,
  }));
  return capability;
};

export const createViewportEvidenceCinePhase = function createViewportEvidenceCinePhase(
  sessionCapability,
  exactOptions,
) {
  if (arguments.length !== 2) return null;
  let sessionState = null;
  try { sessionState = REPORT_SESSION_STATES.get(sessionCapability); } catch (_) { sessionState = null; }
  const snapshot = snapshotExactDataObject(exactOptions, ['originPhaseId']);
  if (!sessionState || sessionState.closed || !snapshot
      || !Number.isSafeInteger(snapshot.originPhaseId) || snapshot.originPhaseId <= 0) return null;
  const capability = Object.freeze({});
  const authorization = Object.freeze({});
  const phaseState = {
    capability,
    authorization,
    session: sessionCapability,
    sessionState,
    originPhaseId: snapshot.originPhaseId,
    cineInteractions: new Set(),
    diagnostics: {
      zeroDeltaCount: 0,
      lastAcceptedNonzero: null,
      lastZeroDelta: null,
      sealed: null,
      readable: true,
    },
    active: true,
  };
  CINE_PHASE_STATES.set(capability, phaseState);
  CINE_AUTHORIZATION_STATES.set(authorization, Object.freeze({
    session: sessionCapability,
    sessionState,
    phaseState,
  }));
  sessionState.cinePhases.add(phaseState);
  return capability;
};

export const installViewportEvidenceCinePhase = capability => {
  let phaseState = null;
  try { phaseState = CINE_PHASE_STATES.get(capability); } catch (_) { phaseState = null; }
  let sessionState = null;
  try { sessionState = installedSession && REPORT_SESSION_STATES.get(installedSession); } catch (_) { sessionState = null; }
  if (!phaseState || !phaseState.active || !sessionState || sessionState.closed
      || phaseState.session !== installedSession || phaseState.sessionState !== sessionState
      || !sessionState.cinePhases.has(phaseState)) return false;
  if (installedCinePhase === capability) return true;
  let previousState = null;
  try {
    previousState = installedCinePhase && CINE_PHASE_STATES.get(installedCinePhase);
  } catch (_) {
    previousState = null;
  }
  if (previousState) closeCinePhaseState(previousState, 'replaced');
  installedCinePhase = capability;
  return true;
};

export const uninstallViewportEvidenceCinePhase = capability => {
  if (!installedCinePhase || (capability !== undefined && capability !== installedCinePhase)) return false;
  let phaseState = null;
  try { phaseState = CINE_PHASE_STATES.get(installedCinePhase); } catch (_) { phaseState = null; }
  if (!phaseState) {
    installedCinePhase = null;
    return false;
  }
  return closeCinePhaseState(phaseState, 'cancelled');
};

export const installViewportEvidenceReportSession = capability => {
  let nextState;
  try { nextState = REPORT_SESSION_STATES.get(capability); } catch (_) { nextState = null; }
  if (!nextState || nextState.closed) return false;
  if (installedSession === capability) return true;
  const previous = installedSession;
  installedSession = null;
  if (previous) {
    const previousState = REPORT_SESSION_STATES.get(previous);
    if (previousState) closeSessionState(previousState, 'replaced');
  }
  installedSession = capability;
  return true;
};

export const uninstallViewportEvidenceReportSession = capability => {
  if (!installedSession) return false;
  if (capability !== undefined && capability !== installedSession) return false;
  const current = installedSession;
  installedSession = null;
  const state = REPORT_SESSION_STATES.get(current);
  return state ? closeSessionState(state) : false;
};

const snapshotEvidenceContext = context => {
  try {
    if (!context || typeof context !== 'object' || Array.isArray(context)) return null;
    const keys = Reflect.ownKeys(context);
    if (keys.length !== EVIDENCE_CONTEXT_KEYS.length
        || keys.some(key => typeof key !== 'string' || !EVIDENCE_CONTEXT_KEYS.includes(key))) return null;
    const snapshot = Object.create(null);
    for (const key of EVIDENCE_CONTEXT_KEYS) {
      const descriptor = Object.getOwnPropertyDescriptor(context, key);
      if (!descriptor || !Object.prototype.hasOwnProperty.call(descriptor, 'value')) return null;
      snapshot[key] = descriptor.value;
    }
    return Object.freeze(snapshot);
  } catch (_) {
    return null;
  }
};

const prepareEnabledOptions = options => {
  try {
    if (!options || typeof options !== 'object' || Array.isArray(options)) return null;
    const benchmarkDescriptor = Object.getOwnPropertyDescriptor(options, 'benchmarkMode');
    if (!benchmarkDescriptor || !Object.prototype.hasOwnProperty.call(benchmarkDescriptor, 'value')
        || benchmarkDescriptor.value !== true) return null;

    const session = installedSession;
    const sessionState = session && REPORT_SESSION_STATES.get(session);
    if (!sessionState || sessionState.closed) return null;

    const keys = Reflect.ownKeys(options);
    if (keys.length !== BEGIN_KEYS.length
        || keys.some(key => typeof key !== 'string' || !BEGIN_KEYS.includes(key))) return null;
    const snapshot = Object.create(null);
    for (const key of BEGIN_KEYS) {
      const descriptor = Object.getOwnPropertyDescriptor(options, key);
      if (!descriptor || !Object.prototype.hasOwnProperty.call(descriptor, 'value')) return null;
      snapshot[key] = descriptor.value;
    }
    return { session, sessionState, snapshot: Object.freeze(snapshot) };
  } catch (_) {
    return null;
  }
};

const allFencesCurrent = snapshot => {
  try {
    return snapshot.isMounted() === true
      && snapshot.isViewportCurrent() === true
      && snapshot.isGenerationCurrent() === true
      && snapshot.isSliceCurrent() === true;
  } catch (_) {
    return false;
  }
};

const renderAuthorityCurrent = snapshot => {
  try { return snapshot.isRenderAuthorityCurrent() === true; } catch (_) { return false; }
};

export const beginViewportEvidenceNetworkRequest = (options, windowLevelBinding = null) => {
  const prepared = prepareEnabledOptions(options);
  if (!prepared) return null;
  const { session, sessionState, snapshot } = prepared;
  if ([
    snapshot.fetchImpl,
    snapshot.blobFactory,
    snapshot.createObjectURL,
    snapshot.revokeObjectURL,
    snapshot.createImage,
    snapshot.isMounted,
    snapshot.isViewportCurrent,
    snapshot.isGenerationCurrent,
    snapshot.isSliceCurrent,
    snapshot.isRenderAuthorityCurrent,
  ].some(value => typeof value !== 'function')) return null;

  let context;
  let canonicalRequestUrl;
  try {
    context = snapshotEvidenceContext(snapshot.context);
    if (!context) return null;
    canonicalRequestUrl = new URL(snapshot.requestUrl, new URL(snapshot.currentOrigin).origin).href;
  } catch (_) {
    return null;
  }
  const windowLevelRenderBinding = resolveWindowLevelRenderBinding({
    context,
    binding: windowLevelBinding,
    sessionState,
  });
  if (!windowLevelRenderBinding) return null;

  let evidence;
  let descriptor;
  try {
    evidence = createViewportEvidenceIdentity({
      benchmarkMode: true,
      context,
      allocateEvidenceOrdinal: () => reserveGlobalOrdinal('evidence'),
      onEvent: event => {
        const eventContext = safeContext(event);
        appendEvent(sessionState, eventContext, {
          type: event.type === 'created' ? 'evidence-created' : 'evidence-terminal',
          evidenceOrdinal: event.evidenceOrdinal,
          ...(event.type === 'terminal' ? { outcome: event.outcome } : {}),
        });
      },
    });
    descriptor = evidence.descriptor();
  } catch (_) {
    return null;
  }
  const retainedContext = safeContext(descriptor);
  const renderAuthority = Object.freeze({
    session,
    sessionState,
    context: retainedContext,
    callbacks: Object.freeze([
      snapshot.isMounted,
      snapshot.isViewportCurrent,
      snapshot.isGenerationCurrent,
      snapshot.isSliceCurrent,
      snapshot.isRenderAuthorityCurrent,
    ]),
  });
  const controller = new AbortController();
  const operationState = {
    operation: null,
    session,
    sessionState,
    context: retainedContext,
    renderAuthority,
    windowLevelInputState: windowLevelRenderBinding.inputState,
    renderWindowLevel: windowLevelRenderBinding.windowLevel,
    cineCommandState: windowLevelRenderBinding.cineCommandState,
    evidenceOrdinal: descriptor.evidenceOrdinal,
    requestOrdinal: null,
    canonicalRequestUrl,
    controller,
    frame: null,
    decodedImage: null,
    coldCacheRootState: null,
    provisionalCacheRootState: null,
    cacheRootState: null,
    frameReleased: false,
    relinquish: null,
    relinquished: false,
    releaseCleanupPreserved: false,
    ready: false,
    published: false,
    publishing: false,
    cancelled: false,
    finished: false,
    acceptedSurfaces: new Set(),
  };

  const operation = Object.freeze({
    ready: () => operationState.readyPromise,
    lineage: () => Object.freeze({
      evidenceOrdinal: operationState.evidenceOrdinal,
      requestOrdinal: operationState.requestOrdinal,
    }),
    createDecodedCacheRoot: options => {
      if (!operationState.ready || !operationState.published || operationState.publishing
          || operationState.cancelled || operationState.finished || sessionState.closed
          || operationState.frameReleased || operationState.coldCacheRootState) return null;
      let canonicalUrl;
      try {
        if (!options || typeof options !== 'object' || Array.isArray(options)) return null;
        const keys = Reflect.ownKeys(options);
        if (keys.length !== 1 || keys[0] !== 'canonicalUrl') return null;
        const descriptorForUrl = Object.getOwnPropertyDescriptor(options, 'canonicalUrl');
        if (!descriptorForUrl || !Object.prototype.hasOwnProperty.call(descriptorForUrl, 'value')) return null;
        canonicalUrl = descriptorForUrl.value;
      } catch (_) {
        return null;
      }
      if (typeof canonicalUrl !== 'string' || canonicalUrl !== operationState.canonicalRequestUrl) return null;
      let cacheRootOrdinal;
      try { cacheRootOrdinal = reserveGlobalOrdinal('cacheRoot'); } catch (_) { return null; }
      if (sessionState.allocatedCacheRootOrdinals.has(cacheRootOrdinal)) return null;
      sessionState.allocatedCacheRootOrdinals.add(cacheRootOrdinal);
      const rootState = {
        root: null,
        sessionState,
        frame: operationState.frame,
        canonicalUrl,
        sourceSlice: retainedContext.slice,
        cacheRootOrdinal,
        image: operationState.decodedImage,
        displayOwned: true,
        frameReleased: false,
        evicted: false,
        cacheOwnershipTransferred: false,
        reservations: 0,
        consumers: new Set(),
      };
      const root = Object.freeze({
        lineage: () => Object.freeze({ cacheRootOrdinal }),
        snapshot: () => Object.freeze({
          cacheRootOrdinal,
          evicted: rootState.evicted,
        }),
        evict: () => evictCacheRootState(rootState),
      });
      rootState.root = root;
      operationState.coldCacheRootState = rootState;
      operationState.provisionalCacheRootState = rootState;
      CACHE_ROOT_STATES.set(root, rootState);
      sessionState.roots.add(root);
      appendEvent(sessionState, retainedContext, {
        type: 'decoded-cache-root-created',
        evidenceOrdinal: operationState.evidenceOrdinal,
        cacheRootOrdinal,
      });
      return root;
    },
    transferDecodedCacheRoot: register => {
      const rootState = operationState.provisionalCacheRootState;
      if (!rootState || rootState.evicted || operationState.cancelled
          || operationState.finished || sessionState.closed || typeof register !== 'function') {
        evictProvisionalCacheRoot(operationState);
        return false;
      }
      let registered = false;
      try { registered = register(rootState.root) === true; } catch (_) { registered = false; }
      if (registered && operationState.provisionalCacheRootState === rootState
          && !rootState.evicted && !operationState.cancelled
          && !operationState.finished && !sessionState.closed) {
        rootState.cacheOwnershipTransferred = true;
        operationState.provisionalCacheRootState = null;
        return true;
      }
      evictProvisionalCacheRoot(operationState);
      return false;
    },
    publish: (publisher, relinquish) => {
      if (!operationState.ready || operationState.cancelled || operationState.finished
          || operationState.published || typeof publisher !== 'function'
          || (relinquish !== undefined && typeof relinquish !== 'function')) return false;
      operationState.relinquish = relinquish || null;
      operationState.publishing = true;
      let published = false;
      try {
        published = operationState.frame.publish(payload => {
          operationState.decodedImage = payload?.image || null;
          return publisher(payload);
        }) === true;
      } catch (_) { published = false; }
      operationState.publishing = false;
      if (published && operationState.cineCommandState
          && !revalidateRenderOperationAuthority(operationState)) published = false;
      operationState.published = published;
      if (!published) {
        releaseFrame(operationState);
        finishOperation(operationState);
      }
      return published;
    },
    acceptedVisible: surface => {
      if (!SAFE_SURFACES.has(surface)) return false;
      if (operationState.cancelled || operationState.finished
          || (!operationState.publishing && !operationState.published)) return false;
      const claim = claimAcceptedVisibleSurface(operationState, surface);
      if (!claim) return false;
      if (claim === 'accepted') {
        appendEvent(sessionState, retainedContext, {
          type: 'accepted-visible',
          evidenceOrdinal: operationState.evidenceOrdinal,
          requestOrdinal: operationState.requestOrdinal,
          surface,
        });
      }
      return true;
    },
    cancel: () => cancelOperation(operationState),
    release: () => {
      if (!operationState.frame) return cancelOperation(operationState);
      evictProvisionalCacheRoot(operationState);
      const released = releaseFrame(operationState);
      finishOperation(operationState);
      return released;
    },
  });
  operationState.operation = operation;
  if (operationState.cineCommandState
      && !viewportCineController.claimRenderOperation(operationState.cineCommandState, operation)) {
    try { evidence.terminalize('failed'); } catch (_) {}
    operationState.cancelled = true;
    operationState.finished = true;
    return null;
  }
  OPERATION_STATES.set(operation, operationState);
  sessionState.operations.add(operation);

  const loaderPromise = loadVisibleJpegNetworkFrame({
    benchmarkMode: true,
    requestUrl: snapshot.requestUrl,
    currentOrigin: snapshot.currentOrigin,
    evidence,
    fetchImpl: snapshot.fetchImpl,
    blobFactory: snapshot.blobFactory,
    createObjectURL: snapshot.createObjectURL,
    revokeObjectURL: snapshot.revokeObjectURL,
    createImage: snapshot.createImage,
    isGenerationCurrent: () => allFencesCurrent(snapshot),
    isAuthorityCurrent: () => renderAuthorityCurrent(snapshot),
    requestAllocator: sessionState.requestAllocator,
    onEvent: event => (
      event.evidenceOrdinal === operationState.evidenceOrdinal
        ? appendEvent(sessionState, retainedContext, {
          type: `network-${event.type}`,
          evidenceOrdinal: event.evidenceOrdinal,
          requestOrdinal: event.requestOrdinal,
        })
        : false
    ),
    signal: controller.signal,
  });

  operationState.readyPromise = Promise.resolve(loaderPromise).then(result => {
    operationState.requestOrdinal = Number.isSafeInteger(result?.requestOrdinal) ? result.requestOrdinal : null;
    if (operationState.cancelled || sessionState.closed) {
      if (result?.frame) {
        operationState.frame = result.frame;
        try { result.frame.cancel(); } catch (_) {}
        releaseFrame(operationState);
      }
      if (result?.cleanup) preserveCleanup(sessionState, () => result.cleanup.retry());
      finishOperation(operationState);
      return false;
    }
    if (result?.ok !== true || !result.frame) {
      if (result?.cleanup) preserveCleanup(sessionState, () => result.cleanup.retry());
      finishOperation(operationState);
      return false;
    }
    operationState.frame = result.frame;
    operationState.ready = true;
    return true;
  }, () => {
    try { evidence.terminalize('failed'); } catch (_) {}
    finishOperation(operationState);
    return false;
  });
  return operation;
};

export const beginViewportEvidencePreloadRequest = options => {
  const prepared = prepareEnabledOptions(options);
  if (!prepared) return null;
  const { sessionState, snapshot } = prepared;
  if ([
    snapshot.fetchImpl,
    snapshot.blobFactory,
    snapshot.createObjectURL,
    snapshot.revokeObjectURL,
    snapshot.createImage,
    snapshot.isMounted,
    snapshot.isViewportCurrent,
    snapshot.isGenerationCurrent,
    snapshot.isSliceCurrent,
    snapshot.isRenderAuthorityCurrent,
  ].some(value => typeof value !== 'function')) return null;

  let context;
  let canonicalRequestUrl;
  try {
    context = snapshotEvidenceContext(snapshot.context);
    if (!context) return null;
    canonicalRequestUrl = new URL(snapshot.requestUrl, new URL(snapshot.currentOrigin).origin).href;
  } catch (_) {
    return null;
  }

  let evidence;
  let descriptor;
  try {
    evidence = createViewportEvidenceIdentity({
      benchmarkMode: true,
      context,
      allocateEvidenceOrdinal: () => reserveGlobalOrdinal('evidence'),
    });
    descriptor = evidence.descriptor();
  } catch (_) {
    return null;
  }

  const retainedContext = safeContext(descriptor);
  const controller = new AbortController();
  const operationState = {
    operation: null,
    sessionState,
    context: retainedContext,
    evidenceOrdinal: descriptor.evidenceOrdinal,
    requestOrdinal: null,
    canonicalRequestUrl,
    controller,
    frame: null,
    decodedImage: null,
    coldCacheRootState: null,
    provisionalCacheRootState: null,
    cacheRootState: null,
    frameReleased: false,
    relinquish: null,
    relinquished: false,
    releaseCleanupPreserved: false,
    ready: false,
    published: false,
    publishing: false,
    cancelled: false,
    finished: false,
    acceptedSurfaces: new Set(),
    preload: true,
    preloadTerminal: false,
  };

  const failPreload = outcome => {
    try { evidence.terminalize(outcome); } catch (_) {}
    appendPreloadTerminal(operationState, outcome);
    if (operationState.frame) {
      try { operationState.frame.cancel(); } catch (_) {}
      releaseFrame(operationState);
    }
    finishOperation(operationState);
    return false;
  };

  const operation = Object.freeze({
    ready: () => operationState.readyPromise,
    lineage: () => Object.freeze({
      evidenceOrdinal: operationState.evidenceOrdinal,
      requestOrdinal: operationState.requestOrdinal,
    }),
    registerPreloadDecodedCacheRoot: registrationOptions => {
      if (!operationState.ready || operationState.cancelled || operationState.finished
          || sessionState.closed || operationState.frameReleased
          || operationState.provisionalCacheRootState) return false;
      let canonicalUrl;
      let register;
      try {
        if (!registrationOptions || typeof registrationOptions !== 'object'
            || Array.isArray(registrationOptions)) return false;
        const keys = Reflect.ownKeys(registrationOptions);
        if (keys.length !== 2 || !keys.includes('canonicalUrl') || !keys.includes('register')) return false;
        const canonicalDescriptor = Object.getOwnPropertyDescriptor(registrationOptions, 'canonicalUrl');
        const registerDescriptor = Object.getOwnPropertyDescriptor(registrationOptions, 'register');
        if (!canonicalDescriptor || !Object.prototype.hasOwnProperty.call(canonicalDescriptor, 'value')
            || !registerDescriptor || !Object.prototype.hasOwnProperty.call(registerDescriptor, 'value')) return false;
        canonicalUrl = canonicalDescriptor.value;
        register = registerDescriptor.value;
      } catch (_) {
        return false;
      }
      if (typeof canonicalUrl !== 'string' || canonicalUrl !== operationState.canonicalRequestUrl
          || typeof register !== 'function') return false;

      let cacheRootOrdinal;
      try { cacheRootOrdinal = reserveGlobalOrdinal('cacheRoot'); } catch (_) { return failPreload('failed'); }
      if (sessionState.allocatedCacheRootOrdinals.has(cacheRootOrdinal)) return failPreload('failed');
      sessionState.allocatedCacheRootOrdinals.add(cacheRootOrdinal);
      const rootState = {
        root: null,
        sessionState,
        frame: operationState.frame,
        canonicalUrl,
        sourceSlice: retainedContext.slice,
        cacheRootOrdinal,
        image: operationState.decodedImage,
        displayOwned: false,
        frameReleased: false,
        evicted: false,
        cacheOwnershipTransferred: false,
        cacheDetach: null,
        cacheDetached: false,
        reservations: 0,
        consumers: new Set(),
        preload: true,
        cleanupComplete: false,
        context: retainedContext,
        evidenceOrdinal: operationState.evidenceOrdinal,
        requestOrdinal: operationState.requestOrdinal,
      };
      const root = Object.freeze({
        lineage: () => Object.freeze({ cacheRootOrdinal }),
        snapshot: () => Object.freeze({ cacheRootOrdinal, evicted: rootState.evicted }),
        evict: () => evictCacheRootState(rootState),
      });
      rootState.root = root;
      operationState.cacheRootState = rootState;
      operationState.provisionalCacheRootState = rootState;
      CACHE_ROOT_STATES.set(root, rootState);
      sessionState.roots.add(root);

      let detach = null;
      try { detach = register(root, operationState.decodedImage); } catch (_) { detach = null; }
      if (typeof detach !== 'function' || operationState.provisionalCacheRootState !== rootState
          || rootState.evicted || operationState.cancelled || operationState.finished
          || sessionState.closed) {
        evictProvisionalCacheRoot(operationState);
        return failPreload('failed');
      }
      rootState.cacheDetach = detach;
      rootState.cacheOwnershipTransferred = true;
      operationState.provisionalCacheRootState = null;
      let terminalized = false;
      try { terminalized = evidence.terminalize('preloaded') === true; } catch (_) { terminalized = false; }
      if (!terminalized) {
        evictCacheRootState(rootState);
        return failPreload('failed');
      }
      appendEvent(sessionState, retainedContext, {
        type: 'preload-root-registered',
        evidenceOrdinal: operationState.evidenceOrdinal,
        requestOrdinal: operationState.requestOrdinal,
        cacheRootOrdinal,
      });
      appendPreloadTerminal(operationState, 'registered');
      finishOperation(operationState);
      return true;
    },
    cancel: () => cancelOperation(operationState),
  });
  operationState.operation = operation;
  OPERATION_STATES.set(operation, operationState);
  sessionState.operations.add(operation);

  const loaderPromise = loadVisibleJpegNetworkFrame({
    benchmarkMode: true,
    requestUrl: snapshot.requestUrl,
    currentOrigin: snapshot.currentOrigin,
    evidence,
    fetchImpl: snapshot.fetchImpl,
    blobFactory: snapshot.blobFactory,
    createObjectURL: snapshot.createObjectURL,
    revokeObjectURL: snapshot.revokeObjectURL,
    createImage: snapshot.createImage,
    isGenerationCurrent: () => allFencesCurrent(snapshot),
    isAuthorityCurrent: () => renderAuthorityCurrent(snapshot),
    requestAllocator: sessionState.requestAllocator,
    onEvent: event => {
      if (event.evidenceOrdinal !== operationState.evidenceOrdinal) return false;
      operationState.requestOrdinal = event.requestOrdinal;
      const type = {
        request: 'preload-requested',
        headers: 'preload-response-validated',
        body: 'preload-body-validated',
        decoded: 'preload-decoded',
      }[event.type];
      return type ? appendEvent(sessionState, retainedContext, {
        type,
        evidenceOrdinal: event.evidenceOrdinal,
        requestOrdinal: event.requestOrdinal,
      }) : false;
    },
    signal: controller.signal,
  });

  operationState.readyPromise = Promise.resolve(loaderPromise).then(result => {
    operationState.requestOrdinal = Number.isSafeInteger(result?.requestOrdinal)
      ? result.requestOrdinal
      : operationState.requestOrdinal;
    if (operationState.cancelled || sessionState.closed) {
      if (result?.frame) {
        operationState.frame = result.frame;
        try { result.frame.cancel(); } catch (_) {}
        releaseFrame(operationState);
      }
      if (result?.cleanup) preserveCleanup(sessionState, () => result.cleanup.retry());
      finishOperation(operationState);
      return false;
    }
    if (result?.ok !== true || !result.frame) {
      if (result?.cleanup) preserveCleanup(sessionState, () => result.cleanup.retry());
      const status = (() => { try { return evidence.status(); } catch (_) { return null; } })();
      appendPreloadTerminal(operationState, status?.outcome || 'failed');
      finishOperation(operationState);
      return false;
    }
    const validatedRoot = claimVisibleJpegValidatedPreloadRoot(result.frame);
    if (!validatedRoot || validatedRoot.frame !== result.frame || !validatedRoot.image) {
      operationState.frame = result.frame;
      return failPreload('failed');
    }
    operationState.frame = validatedRoot.frame;
    operationState.decodedImage = validatedRoot.image;
    operationState.ready = true;
    return true;
  }, () => failPreload('failed'));
  return operation;
};

const prepareWarmEnabledOptions = options => {
  try {
    if (!options || typeof options !== 'object' || Array.isArray(options)) return null;
    const benchmarkDescriptor = Object.getOwnPropertyDescriptor(options, 'benchmarkMode');
    if (!benchmarkDescriptor || !Object.prototype.hasOwnProperty.call(benchmarkDescriptor, 'value')
        || benchmarkDescriptor.value !== true) return null;

    const session = installedSession;
    const sessionState = session && REPORT_SESSION_STATES.get(session);
    if (!sessionState || sessionState.closed) return null;

    const keys = Reflect.ownKeys(options);
    if (keys.length !== WARM_BEGIN_KEYS.length
        || keys.some(key => typeof key !== 'string' || !WARM_BEGIN_KEYS.includes(key))) return null;
    const snapshot = Object.create(null);
    for (const key of WARM_BEGIN_KEYS) {
      const descriptor = Object.getOwnPropertyDescriptor(options, key);
      if (!descriptor || !Object.prototype.hasOwnProperty.call(descriptor, 'value')) return null;
      snapshot[key] = descriptor.value;
    }
    const context = snapshotEvidenceContext(snapshot.context);
    if (!context) return null;
    snapshot.context = context;
    return { session, sessionState, snapshot: Object.freeze(snapshot) };
  } catch (_) {
    return null;
  }
};

const COLD_FALLBACK_ALLOWED = Object.freeze({
  outcome: 'cold-fallback-allowed',
  operation: null,
});

const TERMINAL_STALE_NO_FALLBACK = Object.freeze({
  outcome: 'terminal-stale-no-fallback',
  operation: null,
});

const terminalWarmAttempt = evidenceOrdinal => Object.freeze({
  outcome: 'terminal-warm-attempt',
  operation: null,
  ...(Number.isSafeInteger(evidenceOrdinal) ? { evidenceOrdinal } : {}),
});

const warmRootStateIsAuthoritative = ({ session, sessionState, snapshot, rootState, reservedFrame }) => {
  try {
    return installedSession === session
      && REPORT_SESSION_STATES.get(session) === sessionState
      && !sessionState.closed
      && CACHE_ROOT_STATES.get(snapshot.cacheRoot) === rootState
      && rootState.root === snapshot.cacheRoot
      && rootState.sessionState === sessionState
      && sessionState.roots.has(rootState.root)
      && !rootState.evicted
      && !rootState.frameReleased
      && rootState.cacheOwnershipTransferred === true
      && rootState.frame
      && rootState.image
      && typeof snapshot.canonicalUrl === 'string'
      && rootState.canonicalUrl === snapshot.canonicalUrl
      && rootState.sourceSlice === snapshot.context.slice
      && (!reservedFrame || reservedFrame.image === rootState.image);
  } catch (_) {
    return false;
  }
};

const warmFencesAreAuthoritative = authority => {
  const callbacks = [
    authority.snapshot.isMounted,
    authority.snapshot.isViewportCurrent,
    authority.snapshot.isGenerationCurrent,
    authority.snapshot.isSliceCurrent,
    authority.snapshot.isRenderAuthorityCurrent,
  ];
  for (const callback of callbacks) {
    if (!warmRootStateIsAuthoritative(authority)) return false;
    let current = false;
    try { current = callback() === true; } catch (_) { current = false; }
    if (!current || !warmRootStateIsAuthoritative(authority)) return false;
  }
  return warmRootStateIsAuthoritative(authority);
};

const reserveWarmRootFrame = rootState => {
  if (!rootState || rootState.evicted || rootState.frameReleased
      || rootState.cacheOwnershipTransferred !== true || !rootState.image) return null;
  rootState.reservations += 1;
  let held = true;
  return {
    image: rootState.image,
    commit: operation => {
      if (!held || !operation) return false;
      held = false;
      rootState.reservations -= 1;
      rootState.consumers.add(operation);
      maybeReleaseCacheRootFrame(rootState);
      return true;
    },
    release: () => {
      if (!held) return false;
      held = false;
      rootState.reservations -= 1;
      maybeReleaseCacheRootFrame(rootState);
      return true;
    },
  };
};

const successfulWarmBegin = operation => Object.freeze({
  outcome: 'warm-operation',
  operation,
  lineage: operation.lineage,
  publish: operation.publish,
  acceptedVisible: operation.acceptedVisible,
  cancel: operation.cancel,
  release: operation.release,
});

export const beginViewportEvidenceDecodedCacheHit = (options, windowLevelBinding = null) => {
  const prepared = prepareWarmEnabledOptions(options);
  if (!prepared) return COLD_FALLBACK_ALLOWED;
  const { session, sessionState, snapshot } = prepared;
  if ([
    snapshot.isMounted,
    snapshot.isViewportCurrent,
    snapshot.isGenerationCurrent,
    snapshot.isSliceCurrent,
    snapshot.isRenderAuthorityCurrent,
  ].some(value => typeof value !== 'function')) return COLD_FALLBACK_ALLOWED;
  const windowLevelRenderBinding = resolveWindowLevelRenderBinding({
    context: snapshot.context,
    binding: windowLevelBinding,
    sessionState,
  });
  if (!windowLevelRenderBinding) return TERMINAL_STALE_NO_FALLBACK;

  let rootState;
  try { rootState = CACHE_ROOT_STATES.get(snapshot.cacheRoot); } catch (_) { rootState = null; }
  const authority = { session, sessionState, snapshot, rootState, reservedFrame: null };
  if (!warmRootStateIsAuthoritative(authority)) return COLD_FALLBACK_ALLOWED;
  if (!warmFencesAreAuthoritative(authority)) return TERMINAL_STALE_NO_FALLBACK;
  if (!warmRootStateIsAuthoritative(authority)) return TERMINAL_STALE_NO_FALLBACK;

  const reservedFrame = reserveWarmRootFrame(rootState);
  if (!reservedFrame) return COLD_FALLBACK_ALLOWED;
  authority.reservedFrame = reservedFrame;
  if (!warmFencesAreAuthoritative(authority)
      || !warmRootStateIsAuthoritative(authority)) {
    reservedFrame.release();
    return TERMINAL_STALE_NO_FALLBACK;
  }

  let evidence;
  let descriptor;
  let consumerOrdinal;
  let allocatedEvidenceOrdinal = null;
  try {
    if (!warmRootStateIsAuthoritative(authority)) {
      reservedFrame.release();
      return TERMINAL_STALE_NO_FALLBACK;
    }
    evidence = createViewportEvidenceIdentity({
      benchmarkMode: true,
      context: snapshot.context,
      allocateEvidenceOrdinal: () => {
        allocatedEvidenceOrdinal = reserveGlobalOrdinal('evidence');
        return allocatedEvidenceOrdinal;
      },
      onEvent: event => {
        const eventContext = safeContext(event);
        appendEvent(sessionState, eventContext, {
          type: event.type === 'created' ? 'evidence-created' : 'evidence-terminal',
          evidenceOrdinal: event.evidenceOrdinal,
          ...(event.type === 'terminal' ? { outcome: event.outcome } : {}),
        });
      },
    });
    descriptor = evidence.descriptor();
  } catch (_) {
    try { evidence?.terminalize?.('failed'); } catch (_) {}
    reservedFrame.release();
    return Number.isSafeInteger(allocatedEvidenceOrdinal)
      ? terminalWarmAttempt(allocatedEvidenceOrdinal)
      : TERMINAL_STALE_NO_FALLBACK;
  }

  const failAllocatedWarmAttempt = () => {
    try { evidence.terminalize('failed'); } catch (_) {}
    reservedFrame.release();
    return terminalWarmAttempt(descriptor.evidenceOrdinal);
  };

  if (!warmFencesAreAuthoritative(authority)
      || !warmRootStateIsAuthoritative(authority)) return failAllocatedWarmAttempt();

  try {
    consumerOrdinal = reserveGlobalOrdinal('consumer');
    if (sessionState.allocatedConsumerOrdinals.has(consumerOrdinal)) throw new Error('duplicate consumer');
    sessionState.allocatedConsumerOrdinals.add(consumerOrdinal);
  } catch (_) {
    return failAllocatedWarmAttempt();
  }

  if (!warmFencesAreAuthoritative(authority)
      || !warmRootStateIsAuthoritative(authority)) return failAllocatedWarmAttempt();

  const controller = new AbortController();
  let consumerReleased = false;
  let publishUsed = false;
  let published = false;
  const authorizeWarmPublication = () => {
    if (!warmRootStateIsAuthoritative(authority)
        || !warmFencesAreAuthoritative(authority)
        || !warmRootStateIsAuthoritative(authority)) {
      try { evidence.terminalize('failed'); } catch (_) {}
      return false;
    }
    let authorized = false;
    try {
      authorized = evidence.authorizePublication({
        generationCurrent: true,
        authorityCurrent: true,
      }) === true;
    } catch (_) {
      authorized = false;
    }
    if (!authorized || !warmRootStateIsAuthoritative(authority)) {
      try { evidence.terminalize('failed'); } catch (_) {}
      return false;
    }
    return true;
  };
  const consumerFrame = Object.freeze({
    publish: publisher => {
      if (consumerReleased || publishUsed || typeof publisher !== 'function') return false;
      publishUsed = true;
      if (!authorizeWarmPublication() || !warmRootStateIsAuthoritative(authority)) {
        try { evidence.terminalize('failed'); } catch (_) {}
        consumerReleased = true;
        return false;
      }
      let result = false;
      try { result = publisher(Object.freeze({ image: reservedFrame.image })) === true; } catch (_) { result = false; }
      if (!result || !authorizeWarmPublication()
          || !warmRootStateIsAuthoritative(authority)) {
        try { evidence.terminalize('failed'); } catch (_) {}
        consumerReleased = true;
        return false;
      }
      try { published = evidence.terminalize('published') === true; } catch (_) { published = false; }
      if (!published || !warmRootStateIsAuthoritative(authority)) {
        if (published) published = false;
        consumerReleased = true;
      }
      return published;
    },
    cancel: () => {
      if (consumerReleased) return false;
      consumerReleased = true;
      try { evidence.terminalize(publishUsed && !published ? 'failed' : 'cancelled'); } catch (_) {}
      return true;
    },
    release: () => {
      if (consumerReleased) return false;
      consumerReleased = true;
      if (!published) {
        try { evidence.terminalize('cancelled'); } catch (_) {}
      }
      return true;
    },
    snapshot: () => Object.freeze({ revocationPending: false }),
  });

  const retainedContext = safeContext(descriptor);
  const renderAuthority = Object.freeze({
    session,
    sessionState,
    context: retainedContext,
    callbacks: Object.freeze([
      snapshot.isMounted,
      snapshot.isViewportCurrent,
      snapshot.isGenerationCurrent,
      snapshot.isSliceCurrent,
      snapshot.isRenderAuthorityCurrent,
    ]),
  });
  const lineage = Object.freeze({
    evidenceOrdinal: descriptor.evidenceOrdinal,
    cacheRootOrdinal: rootState.cacheRootOrdinal,
    consumerOrdinal,
  });
  const operationState = {
    operation: null,
    session,
    sessionState,
    context: retainedContext,
    renderAuthority,
    windowLevelInputState: windowLevelRenderBinding.inputState,
    renderWindowLevel: windowLevelRenderBinding.windowLevel,
    cineCommandState: windowLevelRenderBinding.cineCommandState,
    evidenceOrdinal: descriptor.evidenceOrdinal,
    requestOrdinal: null,
    controller,
    frame: consumerFrame,
    coldCacheRootState: null,
    provisionalCacheRootState: null,
    cacheRootState: rootState,
    frameReleased: false,
    relinquish: null,
    relinquished: false,
    releaseCleanupPreserved: false,
    ready: true,
    published: false,
    publishing: false,
    cancelled: false,
    finished: false,
    acceptedSurfaces: new Set(),
  };

  const operation = Object.freeze({
    lineage: () => lineage,
    publish: (publisher, relinquish) => {
      if (operationState.cancelled || operationState.finished || operationState.published
          || typeof publisher !== 'function'
          || (relinquish !== undefined && typeof relinquish !== 'function')) return false;
      operationState.relinquish = relinquish || null;
      operationState.publishing = true;
      let visiblePublished = false;
      try {
        visiblePublished = operationState.frame.publish(payload => publisher(Object.freeze({ image: payload.image }))) === true;
      } catch (_) {
        visiblePublished = false;
      }
      operationState.publishing = false;
      if (visiblePublished && operationState.cineCommandState
          && (!warmRootStateIsAuthoritative(authority)
            || !warmFencesAreAuthoritative(authority)
            || !revalidateRenderOperationAuthority(operationState))) visiblePublished = false;
      operationState.published = visiblePublished;
      if (!visiblePublished) {
        releaseFrame(operationState);
        finishOperation(operationState);
      }
      return visiblePublished;
    },
    acceptedVisible: surface => {
      if (!SAFE_SURFACES.has(surface) || operationState.cancelled || operationState.finished
          || (!operationState.publishing && !operationState.published)
          || (operationState.publishing && (
            !warmRootStateIsAuthoritative(authority)
            || !warmFencesAreAuthoritative(authority)
            || !warmRootStateIsAuthoritative(authority)
          ))) return false;
      const claim = claimAcceptedVisibleSurface(operationState, surface);
      if (!claim) return false;
      if (claim === 'accepted') {
        appendEvent(sessionState, retainedContext, {
          type: 'accepted-visible',
          ...lineage,
          surface,
        });
      }
      return true;
    },
    cancel: () => cancelOperation(operationState),
    release: () => {
      if (operationState.finished || operationState.frameReleased) return false;
      const released = releaseFrame(operationState);
      finishOperation(operationState);
      return released;
    },
  });
  operationState.operation = operation;
  if (!warmFencesAreAuthoritative(authority)
      || !warmRootStateIsAuthoritative(authority)) return failAllocatedWarmAttempt();
  if (!reservedFrame.commit(operation)) {
    try { evidence.terminalize('failed'); } catch (_) {}
    return terminalWarmAttempt(descriptor.evidenceOrdinal);
  }
  const failCommittedWarmAttempt = () => {
    try { evidence.terminalize('failed'); } catch (_) {}
    try { consumerFrame.cancel(); } catch (_) {}
    operationState.frameReleased = true;
    finishOperation(operationState);
    return terminalWarmAttempt(descriptor.evidenceOrdinal);
  };
  if (!warmFencesAreAuthoritative(authority)
      || !warmRootStateIsAuthoritative(authority)) return failCommittedWarmAttempt();
  if (operationState.cineCommandState
      && !viewportCineController.claimRenderOperation(operationState.cineCommandState, operation)) {
    return failCommittedWarmAttempt();
  }
  OPERATION_STATES.set(operation, operationState);
  if (!warmRootStateIsAuthoritative(authority)) return failCommittedWarmAttempt();
  sessionState.operations.add(operation);
  if (!warmRootStateIsAuthoritative(authority)) {
    cancelOperation(operationState);
    return terminalWarmAttempt(descriptor.evidenceOrdinal);
  }
  appendEvent(sessionState, retainedContext, {
    type: 'decoded-cache-consumer-created',
    ...lineage,
  });
  return successfulWarmBegin(operation);
};

const WINDOW_LEVEL_DRAIN_BASE_KEYS = Object.freeze([
  'sequence', 'phase', 'viewport', 'generation', 'interactionOrdinal', 'type',
]);
const WINDOW_LEVEL_DRAIN_EVENT_KEYS = Object.freeze({
  'window-level-trusted-start': Object.freeze([...WINDOW_LEVEL_DRAIN_BASE_KEYS, 'atMs', 'ww', 'wc']),
  'window-level-input': Object.freeze([...WINDOW_LEVEL_DRAIN_BASE_KEYS, 'inputOrdinal', 'atMs', 'ww', 'wc']),
  'window-level-input-coalesced': Object.freeze([
    ...WINDOW_LEVEL_DRAIN_BASE_KEYS, 'inputOrdinal', 'supersededByInputOrdinal', 'atMs', 'ww', 'wc',
  ]),
  'window-level-input-applied': Object.freeze([...WINDOW_LEVEL_DRAIN_BASE_KEYS, 'inputOrdinal', 'atMs', 'ww', 'wc']),
  'window-level-render-publication': Object.freeze([
    ...WINDOW_LEVEL_DRAIN_BASE_KEYS, 'inputOrdinal', 'evidenceOrdinal', 'atMs', 'ww', 'wc',
  ]),
  'window-level-paint': Object.freeze([
    ...WINDOW_LEVEL_DRAIN_BASE_KEYS, 'inputOrdinal', 'evidenceOrdinal', 'atMs', 'ww', 'wc',
  ]),
  'window-level-release': Object.freeze([...WINDOW_LEVEL_DRAIN_BASE_KEYS, 'inputOrdinal', 'atMs', 'ww', 'wc']),
  'window-level-terminal-closed': Object.freeze([
    ...WINDOW_LEVEL_DRAIN_BASE_KEYS, 'outcome', 'atMs', 'ww', 'wc',
  ]),
  'window-level-terminal-settled': Object.freeze([
    ...WINDOW_LEVEL_DRAIN_BASE_KEYS, 'outcome', 'atMs', 'ww', 'wc', 'inputOrdinal', 'evidenceOrdinal',
  ]),
});

const isClosedFrozenDataDescriptor = (descriptor, enumerable) => Boolean(descriptor
  && Object.prototype.hasOwnProperty.call(descriptor, 'value')
  && descriptor.enumerable === enumerable
  && descriptor.writable === false
  && descriptor.configurable === false);

const snapshotDeeplyFrozenOrdinaryArray = value => {
  try {
    if (!Array.isArray(value) || Object.getPrototypeOf(value) !== Array.prototype
        || !Object.isFrozen(value)) return null;
    const ownKeys = Reflect.ownKeys(value);
    const lengthDescriptor = Object.getOwnPropertyDescriptor(value, 'length');
    if (!isClosedFrozenDataDescriptor(lengthDescriptor, false)
        || !Number.isSafeInteger(lengthDescriptor.value) || lengthDescriptor.value < 0
        || ownKeys.length !== lengthDescriptor.value + 1
        || ownKeys[ownKeys.length - 1] !== 'length') return null;
    const snapshot = [];
    for (let index = 0; index < lengthDescriptor.value; index += 1) {
      const key = String(index);
      if (ownKeys[index] !== key) return null;
      const descriptor = Object.getOwnPropertyDescriptor(value, key);
      if (!isClosedFrozenDataDescriptor(descriptor, true)) return null;
      snapshot.push(descriptor.value);
    }
    return Object.freeze(snapshot);
  } catch (_) {
    return null;
  }
};

const snapshotDeeplyFrozenOrdinaryRecord = (value, exactKeys = null, seen = new Set()) => {
  try {
    if (!value || typeof value !== 'object' || Array.isArray(value)
        || Object.getPrototypeOf(value) !== Object.prototype || !Object.isFrozen(value)
        || seen.has(value)) return null;
    seen.add(value);
    const ownKeys = Reflect.ownKeys(value);
    if (ownKeys.some(key => typeof key !== 'string')
        || (exactKeys && (ownKeys.length !== exactKeys.length
          || ownKeys.some(key => !exactKeys.includes(key))))) return null;
    const snapshot = Object.create(null);
    for (const key of ownKeys) {
      const descriptor = Object.getOwnPropertyDescriptor(value, key);
      if (!isClosedFrozenDataDescriptor(descriptor, true)) return null;
      const child = descriptor.value;
      if (child && typeof child === 'object') {
        const childSnapshot = Array.isArray(child)
          ? snapshotDeeplyFrozenOrdinaryArray(child)
          : snapshotDeeplyFrozenOrdinaryRecord(child, null, seen);
        if (!childSnapshot) return null;
      }
      snapshot[key] = child;
    }
    return Object.freeze(snapshot);
  } catch (_) {
    return null;
  }
};

const CINE_DRAIN_BASE_KEYS = Object.freeze([
  'sequence', 'phase', 'originPhaseId', 'cineId', 'viewport', 'seriesGeneration',
  'interaction', 'direction', 'type',
]);
const CINE_DRAIN_EVENT_KEYS = Object.freeze({
  'cine-input': Object.freeze([...CINE_DRAIN_BASE_KEYS, 'inputOrdinal', 'eventKind', 'isTrusted',
    'browserOrdinal', 'phaseWorkId', 'atMs', 'targetBefore', 'targetAfter']),
  'cine-input-applied': Object.freeze([...CINE_DRAIN_BASE_KEYS, 'inputOrdinal', 'commandWorkId',
    'atMs', 'targetBefore', 'targetAfter']),
  'cine-render-publication': Object.freeze([...CINE_DRAIN_BASE_KEYS, 'inputOrdinal', 'commandWorkId',
    'browserOrdinal', 'phaseWorkId', 'atMs', 'generationOrdinal', 'generation', 'requestGeneration',
    'slice', 'surface',
    'connected', 'nonzero', 'active', 'visible', 'focused', 'uncovered', 'sourcePresent',
    'generationOwning']),
  'cine-paint-confirmation': Object.freeze([...CINE_DRAIN_BASE_KEYS, 'inputOrdinal', 'commandWorkId',
    'browserOrdinal', 'phaseWorkId', 'atMs', 'generation', 'slice', 'stableThroughPaint']),
  'cine-release': Object.freeze([...CINE_DRAIN_BASE_KEYS, 'eventKind', 'browserOrdinal', 'atMs']),
  'cine-settlement': Object.freeze([...CINE_DRAIN_BASE_KEYS, 'browserOrdinal', 'phaseWorkId', 'atMs',
    'finalTargetSlice', 'finalRenderedSlice', 'outstandingWorkIds']),
  'cine-input-coalesced': Object.freeze([...CINE_DRAIN_BASE_KEYS, 'inputOrdinal',
    'supersededByInputOrdinal', 'atMs']),
  'cine-command-coalesced': Object.freeze([...CINE_DRAIN_BASE_KEYS, 'inputOrdinal', 'commandWorkId',
    'supersededByInputOrdinal', 'atMs']),
  'cine-terminal': Object.freeze([...CINE_DRAIN_BASE_KEYS, 'reason', 'atMs']),
});
const CINE_DRAIN_INPUT_KINDS = Object.freeze({
  wheel: 'wheel', drag: 'pointer-move', slider: 'slider-change',
});
const CINE_DRAIN_RELEASE_KINDS = Object.freeze({
  wheel: 'wheel-quiet', drag: 'pointer-up', slider: 'slider-release',
});
const safeCineTime = value => typeof value === 'number' && Number.isFinite(value)
  && value >= 0 && value <= Number.MAX_SAFE_INTEGER;
const positiveCineOrdinal = value => Number.isSafeInteger(value) && value > 0;
const nonNegativeCineInteger = value => Number.isSafeInteger(value) && value >= 0;
const CINE_SUCCESSFUL_ATTEMPT_SPAN_KEYS = Object.freeze([
  'firstInputSequence', 'firstInputAtMs', 'settlementSequence', 'settlementAtMs',
]);

const successfulCineAttemptSpansAreStrictlySequential = spans => {
  let prior = null;
  for (const span of spans) {
    if (!positiveCineOrdinal(span?.firstInputSequence)
        || !positiveCineOrdinal(span?.settlementSequence)
        || !safeCineTime(span?.firstInputAtMs) || !safeCineTime(span?.settlementAtMs)
        || span.firstInputSequence >= span.settlementSequence
        || span.firstInputAtMs > span.settlementAtMs
        || (prior && (!(span.firstInputSequence > prior.settlementSequence)
          || span.firstInputAtMs < prior.settlementAtMs))) return false;
    prior = span;
  }
  return true;
};

// Test-only view of the internal successful-attempt span gate. Production has
// no capability to mint or inspect retained rows, and this adapter exposes no
// report fields or controller authority.
export const deriveViewportCineSuccessfulAttemptSpansForTest = spans => {
  if (!WINDOW_LEVEL_TEST_CAPABILITIES_ENABLED) return null;
  const values = snapshotDeeplyFrozenOrdinaryArray(spans);
  if (!values) return Object.freeze([]);
  const snapshots = [];
  for (const value of values) {
    const span = snapshotDeeplyFrozenOrdinaryRecord(value, CINE_SUCCESSFUL_ATTEMPT_SPAN_KEYS);
    if (!span) return Object.freeze([]);
    snapshots.push(Object.freeze({
      firstInputSequence: span.firstInputSequence,
      firstInputAtMs: span.firstInputAtMs,
      settlementSequence: span.settlementSequence,
      settlementAtMs: span.settlementAtMs,
    }));
  }
  return successfulCineAttemptSpansAreStrictlySequential(snapshots)
    ? Object.freeze(snapshots) : Object.freeze([]);
};

const snapshotCineDrainEvent = event => {
  const initial = snapshotDeeplyFrozenOrdinaryRecord(event);
  const keys = initial && CINE_DRAIN_EVENT_KEYS[initial.type];
  if (!keys) return null;
  const row = snapshotDeeplyFrozenOrdinaryRecord(event, keys);
  if (!row || row.phase !== 'cine' || !positiveCineOrdinal(row.sequence)
      || !positiveCineOrdinal(row.originPhaseId) || !positiveCineOrdinal(row.cineId)
      || !nonNegativeCineInteger(row.viewport) || !positiveCineOrdinal(row.seriesGeneration)
      || !['wheel', 'drag', 'slider'].includes(row.interaction)
      || !['forward', 'backward'].includes(row.direction)
      || !safeCineTime(row.atMs)) return null;
  for (const key of ['inputOrdinal', 'supersededByInputOrdinal', 'commandWorkId', 'browserOrdinal',
    'phaseWorkId', 'generationOrdinal', 'generation', 'requestGeneration']) {
    if (Object.prototype.hasOwnProperty.call(row, key) && !positiveCineOrdinal(row[key])) return null;
  }
  for (const key of ['targetBefore', 'targetAfter', 'slice', 'finalTargetSlice', 'finalRenderedSlice']) {
    if (Object.prototype.hasOwnProperty.call(row, key) && !nonNegativeCineInteger(row[key])) return null;
  }
  if (row.type === 'cine-settlement'
      && (!Array.isArray(row.outstandingWorkIds) || row.outstandingWorkIds.length !== 0)) return null;
  if (row.type === 'cine-terminal'
      && !['failed', 'cancelled', 'replaced', 'overflow'].includes(row.reason)) return null;
  return row;
};

const sameCineDrainIdentity = (row, first) => row.phase === first.phase
  && row.originPhaseId === first.originPhaseId && row.cineId === first.cineId
  && row.viewport === first.viewport && row.seriesGeneration === first.seriesGeneration
  && row.interaction === first.interaction && row.direction === first.direction;

const freezeCineWork = (row, owner, kind, ordinal) => Object.freeze({
  workId: row.phaseWorkId,
  originPhaseId: owner.originPhaseId,
  ownerKind: 'cine',
  ownerId: owner.cineId,
  ownerEventOrdinal: ordinal,
  kind,
  outcome: 'completed',
  classification: 'matched',
});

const terminalOutcome = reason => ['failed', 'overflow'].includes(reason) ? 'failed' : 'aborted';
const freezeTerminalCineWork = (workId, owner, kind, ownerEventOrdinal) => Object.freeze({
  workId,
  originPhaseId: owner.originPhaseId,
  ownerKind: 'cine',
  ownerId: owner.cineId,
  ownerEventOrdinal,
  kind,
  outcome: terminalOutcome(owner.reason),
  classification: 'matched',
});

const deriveTerminalCineGroup = (group, first, terminal) => {
  if (group.at(-1) !== terminal || group.some(row => row.type === 'cine-settlement')) return null;
  const rows = group.slice(0, -1);
  const inputs = rows.filter(row => row.type === 'cine-input');
  const commands = rows.filter(row => row.type === 'cine-input-applied');
  const publications = rows.filter(row => row.type === 'cine-render-publication');
  const paints = rows.filter(row => row.type === 'cine-paint-confirmation');
  const inputCoalesced = rows.filter(row => row.type === 'cine-input-coalesced');
  const commandCoalesced = rows.filter(row => row.type === 'cine-command-coalesced');
  const releases = rows.filter(row => row.type === 'cine-release');
  if (rows.length !== inputs.length + commands.length + publications.length + paints.length
      + inputCoalesced.length + commandCoalesced.length + releases.length
      || releases.length > 1) return null;
  const uniqueBy = (values, key) => values.length === new Set(values.map(row => row[key])).size;
  if (!uniqueBy(inputs, 'inputOrdinal') || !uniqueBy(inputs, 'browserOrdinal')
      || !uniqueBy(commands, 'commandWorkId') || !uniqueBy(publications, 'browserOrdinal')
      || !uniqueBy(paints, 'browserOrdinal')) return null;
  const workIds = new Set();
  const addWorkId = workId => {
    if (!positiveCineOrdinal(workId) || workIds.has(workId)) return false;
    workIds.add(workId);
    return true;
  };
  for (const input of inputs) {
    if (input.eventKind !== CINE_DRAIN_INPUT_KINDS[first.interaction] || input.isTrusted !== true
        || input.targetBefore === input.targetAfter
        || (first.direction === 'forward' ? input.targetAfter <= input.targetBefore
          : input.targetAfter >= input.targetBefore)
        || !addWorkId(input.phaseWorkId)) return null;
  }
  for (const command of commands) {
    const matches = inputs.filter(input => input.inputOrdinal === command.inputOrdinal
      && input.sequence < command.sequence && input.targetBefore === command.targetBefore
      && input.targetAfter === command.targetAfter);
    if (matches.length !== 1 || !addWorkId(command.commandWorkId)) return null;
  }
  for (const publication of publications) {
    const matchingCommand = commands.filter(command => command.inputOrdinal === publication.inputOrdinal
      && command.commandWorkId === publication.commandWorkId && command.sequence < publication.sequence);
    const matchingInput = inputs.filter(input => input.inputOrdinal === publication.inputOrdinal);
    if (matchingCommand.length !== 1 || matchingInput.length !== 1
        || publication.generation !== first.seriesGeneration
        || publication.slice !== matchingInput[0].targetAfter
        || !['preview', 'active-image'].includes(publication.surface)
        || ['connected', 'nonzero', 'active', 'visible', 'focused', 'uncovered', 'sourcePresent',
          'generationOwning'].some(key => publication[key] !== true)
        || !addWorkId(publication.phaseWorkId)) return null;
  }
  for (const paint of paints) {
    const matchingPublication = publications.filter(publication => publication.inputOrdinal === paint.inputOrdinal
      && publication.commandWorkId === paint.commandWorkId && publication.sequence < paint.sequence);
    if (matchingPublication.length !== 1 || paint.generation !== first.seriesGeneration
        || paint.slice !== matchingPublication[0].slice || paint.stableThroughPaint !== true
        || !addWorkId(paint.phaseWorkId)) return null;
  }
  for (const coalesced of inputCoalesced) {
    const prior = inputs.filter(input => input.inputOrdinal === coalesced.inputOrdinal
      && input.sequence < coalesced.sequence);
    const next = inputs.filter(input => input.inputOrdinal === coalesced.supersededByInputOrdinal
      && coalesced.sequence < input.sequence);
    if (prior.length !== 1 || next.length !== 1
        || inputCoalesced.filter(row => row.inputOrdinal === coalesced.inputOrdinal).length !== 1) return null;
  }
  for (const coalesced of commandCoalesced) {
    const command = commands.filter(row => row.inputOrdinal === coalesced.inputOrdinal
      && row.commandWorkId === coalesced.commandWorkId && row.sequence < coalesced.sequence);
    const next = inputs.filter(input => input.inputOrdinal === coalesced.supersededByInputOrdinal
      && input.sequence < coalesced.sequence);
    if (command.length !== 1 || next.length !== 1
        || commandCoalesced.filter(row => row.commandWorkId === coalesced.commandWorkId).length !== 1) return null;
  }
  if (releases.length === 1 && (releases[0].eventKind !== CINE_DRAIN_RELEASE_KINDS[first.interaction]
      || (inputs.length > 0 && inputs.at(-1).sequence >= releases[0].sequence))) return null;

  const terminalBase = {
    cineId: first.cineId,
    originPhaseId: first.originPhaseId,
    viewportIndex: first.viewport,
    interaction: first.interaction,
    direction: first.direction,
    reason: terminal.reason,
    terminalEventOrdinal: terminal.sequence,
    atMs: terminal.atMs,
  };
  const events = [];
  const work = [];
  for (const row of rows) {
    let event = null;
    if (row.type === 'cine-input') {
      event = { kind: 'input', attemptEventOrdinal: row.sequence, inputOrdinal: row.inputOrdinal,
        workId: row.phaseWorkId, browserOrdinal: row.browserOrdinal, atMs: row.atMs,
        targetBefore: row.targetBefore, targetAfter: row.targetAfter };
      work.push(freezeTerminalCineWork(row.phaseWorkId, { ...terminalBase }, 'input', row.browserOrdinal));
    } else if (row.type === 'cine-input-applied') {
      const input = inputs.find(candidate => candidate.inputOrdinal === row.inputOrdinal);
      event = { kind: 'command', attemptEventOrdinal: row.sequence, inputOrdinal: row.inputOrdinal,
        workId: row.commandWorkId, atMs: row.atMs, targetBefore: row.targetBefore, targetAfter: row.targetAfter };
      work.push(freezeTerminalCineWork(row.commandWorkId, { ...terminalBase }, 'command', input.browserOrdinal));
    } else if (row.type === 'cine-render-publication') {
      event = { kind: 'accepted-publication', attemptEventOrdinal: row.sequence, inputOrdinal: row.inputOrdinal,
        commandWorkId: row.commandWorkId, workId: row.phaseWorkId, browserOrdinal: row.browserOrdinal,
        atMs: row.atMs, generationOrdinal: row.generationOrdinal, slice: row.slice };
      work.push(freezeTerminalCineWork(row.phaseWorkId, { ...terminalBase },
        'accepted-publication', row.browserOrdinal));
    } else if (row.type === 'cine-paint-confirmation') {
      event = { kind: 'paint-confirmation', attemptEventOrdinal: row.sequence, inputOrdinal: row.inputOrdinal,
        commandWorkId: row.commandWorkId, workId: row.phaseWorkId, browserOrdinal: row.browserOrdinal,
        atMs: row.atMs, generation: row.generation, slice: row.slice };
      work.push(freezeTerminalCineWork(row.phaseWorkId, { ...terminalBase },
        'paint-confirmation', row.browserOrdinal));
    } else if (row.type === 'cine-input-coalesced') {
      event = { kind: 'input-coalesced', attemptEventOrdinal: row.sequence, inputOrdinal: row.inputOrdinal,
        supersededByInputOrdinal: row.supersededByInputOrdinal, atMs: row.atMs };
    } else if (row.type === 'cine-command-coalesced') {
      event = { kind: 'command-coalesced', attemptEventOrdinal: row.sequence, inputOrdinal: row.inputOrdinal,
        commandWorkId: row.commandWorkId, supersededByInputOrdinal: row.supersededByInputOrdinal, atMs: row.atMs };
    } else if (row.type === 'cine-release') {
      event = { kind: 'release', attemptEventOrdinal: row.sequence, eventKind: row.eventKind,
        browserOrdinal: row.browserOrdinal, atMs: row.atMs };
    }
    if (!event) return null;
    events.push(Object.freeze(event));
  }
  return Object.freeze({ terminal: true, terminalBase: Object.freeze({
    ...terminalBase, events: Object.freeze(events),
  }), work: Object.freeze(work) });
};

const deriveCineGroup = group => {
  group.sort((left, right) => left.sequence - right.sequence);
  const first = group[0];
  if (!first || group.some((row, index) => !sameCineDrainIdentity(row, first)
      || (index > 0 && (row.sequence <= group[index - 1].sequence || row.atMs < group[index - 1].atMs)))) return null;
  const terminals = group.filter(row => row.type === 'cine-terminal');
  if (terminals.length > 0) return terminals.length === 1
    ? deriveTerminalCineGroup(group, first, terminals[0]) : null;
  if (group.some(row => row.type === 'cine-input-coalesced' || row.type === 'cine-command-coalesced')) return null;
  const inputs = group.filter(row => row.type === 'cine-input');
  const applied = group.filter(row => row.type === 'cine-input-applied');
  const publications = group.filter(row => row.type === 'cine-render-publication');
  const paints = group.filter(row => row.type === 'cine-paint-confirmation');
  const releases = group.filter(row => row.type === 'cine-release');
  const settlements = group.filter(row => row.type === 'cine-settlement');
  if (inputs.length < 4 || applied.length !== inputs.length || publications.length !== inputs.length
      || paints.length !== inputs.length || releases.length !== 1 || settlements.length !== 1) return null;
  const proofInputs = [];
  const proofPublications = [];
  const work = [];
  const workIds = new Set();
  let priorInput = null;
  let priorPublication = null;
  for (const input of inputs) {
    const matchingApplied = applied.filter(row => row.inputOrdinal === input.inputOrdinal);
    if (matchingApplied.length !== 1) return null;
    const appliedRow = matchingApplied[0];
    const matchingPublication = publications.filter(row => row.inputOrdinal === input.inputOrdinal
      && row.commandWorkId === appliedRow.commandWorkId);
    const matchingPaint = paints.filter(row => row.inputOrdinal === input.inputOrdinal
      && row.commandWorkId === appliedRow.commandWorkId);
    if (matchingPublication.length !== 1 || matchingPaint.length !== 1) return null;
    const publication = matchingPublication[0];
    const paint = matchingPaint[0];
    if (!(input.sequence < appliedRow.sequence && appliedRow.sequence < publication.sequence
        && publication.sequence < paint.sequence)
        || input.eventKind !== CINE_DRAIN_INPUT_KINDS[first.interaction] || input.isTrusted !== true
        || input.targetBefore !== appliedRow.targetBefore || input.targetAfter !== appliedRow.targetAfter
        || input.targetBefore === input.targetAfter
        || (first.direction === 'forward' ? input.targetAfter <= input.targetBefore
          : input.targetAfter >= input.targetBefore)
        || publication.generation !== first.seriesGeneration || paint.generation !== first.seriesGeneration
        || publication.slice !== input.targetAfter || paint.slice !== input.targetAfter
        || (publication.surface !== 'preview' && publication.surface !== 'active-image')
        || ['connected', 'nonzero', 'active', 'visible', 'focused', 'uncovered', 'sourcePresent',
          'generationOwning'].some(key => publication[key] !== true)
        || paint.stableThroughPaint !== true
        || !(input.browserOrdinal < publication.browserOrdinal
          && publication.browserOrdinal < paint.browserOrdinal)
        || !(input.atMs <= publication.atMs && publication.atMs <= paint.atMs)) return null;
    if (priorInput && (priorInput.targetAfter !== input.targetBefore
        || !(priorInput.browserOrdinal < input.browserOrdinal && priorInput.atMs <= input.atMs))) return null;
    if (priorPublication && (!(priorPublication.paint.browserOrdinal < publication.browserOrdinal)
        || !(priorPublication.paint.atMs <= publication.atMs)
        || !(priorPublication.publication.generationOrdinal < publication.generationOrdinal)
        || (first.direction === 'forward' ? priorPublication.publication.slice >= publication.slice
          : priorPublication.publication.slice <= publication.slice))) return null;
    const ownedRows = [input, publication, paint];
    if (ownedRows.some(row => workIds.has(row.phaseWorkId))) return null;
    ownedRows.forEach(row => workIds.add(row.phaseWorkId));
    proofInputs.push(Object.freeze({
      cineId: first.cineId,
      originPhaseId: first.originPhaseId,
      viewportIndex: first.viewport,
      eventKind: input.eventKind,
      isTrusted: true,
      browserOrdinal: input.browserOrdinal,
      atMs: input.atMs,
      targetBefore: input.targetBefore,
      targetAfter: input.targetAfter,
    }));
    proofPublications.push(Object.freeze({
      cineId: first.cineId,
      originPhaseId: first.originPhaseId,
      viewportIndex: first.viewport,
      generationOrdinal: publication.generationOrdinal,
      requestGeneration: publication.requestGeneration,
      slice: publication.slice,
      targetSlice: input.targetAfter,
      renderedSlice: paint.slice,
      publicationBrowserOrdinal: publication.browserOrdinal,
      publicationAtMs: publication.atMs,
      paintBrowserOrdinal: paint.browserOrdinal,
      paintAtMs: paint.atMs,
      surface: publication.surface,
      connected: true,
      nonzero: true,
      active: true,
      visible: true,
      focused: true,
      uncovered: true,
      sourcePresent: true,
      generationOwning: true,
      stableThroughPaint: true,
    }));
    work.push(freezeCineWork(input, first, 'input', input.browserOrdinal));
    work.push(freezeCineWork(publication, first, 'accepted-publication', publication.browserOrdinal));
    work.push(freezeCineWork(paint, first, 'paint-confirmation', paint.browserOrdinal));
    priorInput = input;
    priorPublication = { publication, paint };
  }
  const release = releases[0];
  const settlement = settlements[0];
  if (release.eventKind !== CINE_DRAIN_RELEASE_KINDS[first.interaction]
      || !(inputs.at(-1).browserOrdinal < release.browserOrdinal)
      || !(inputs.at(-1).atMs <= release.atMs)
      || !(Math.max(paints.at(-1).sequence, release.sequence) < settlement.sequence)
      || !(paints.at(-1).browserOrdinal < settlement.browserOrdinal
        && release.browserOrdinal < settlement.browserOrdinal)
      || !(paints.at(-1).atMs <= settlement.atMs && release.atMs <= settlement.atMs)
      || settlement.finalTargetSlice !== inputs.at(-1).targetAfter
      || settlement.finalRenderedSlice !== paints.at(-1).slice
      || settlement.finalTargetSlice !== settlement.finalRenderedSlice
      || workIds.has(settlement.phaseWorkId)) return null;
  work.push(freezeCineWork(settlement, first, 'settlement', settlement.browserOrdinal));
  const proof = Object.freeze({
    cineId: first.cineId,
    priorTerminalCineId: null,
    originPhaseId: first.originPhaseId,
    viewportIndex: first.viewport,
    interaction: first.interaction,
    direction: first.direction,
    inputs: Object.freeze(proofInputs),
    publications: Object.freeze(proofPublications),
    release: Object.freeze({
      cineId: first.cineId,
      originPhaseId: first.originPhaseId,
      viewportIndex: first.viewport,
      eventKind: release.eventKind,
      browserOrdinal: release.browserOrdinal,
      atMs: release.atMs,
    }),
    settlement: Object.freeze({
      cineId: first.cineId,
      originPhaseId: first.originPhaseId,
      viewportIndex: first.viewport,
      browserOrdinal: settlement.browserOrdinal,
      atMs: settlement.atMs,
      finalTargetSlice: settlement.finalTargetSlice,
      finalRenderedSlice: settlement.finalRenderedSlice,
      outstandingWorkIds: Object.freeze([]),
    }),
  });
  return Object.freeze({
    terminal: false,
    proof,
    work: Object.freeze(work),
    span: Object.freeze({
      firstInputSequence: inputs[0].sequence,
      firstInputAtMs: inputs[0].atMs,
      settlementSequence: settlement.sequence,
      settlementAtMs: settlement.atMs,
    }),
  });
};

export const deriveViewportCineProofs = events => {
  try {
    // Consume private drain authority before reflection so copies, proxies, and retries are non-invoking.
    if (!viewportCineController.consumeDrain(events)) return Object.freeze([]);
    const retainedEvents = snapshotDeeplyFrozenOrdinaryArray(events);
    if (!retainedEvents) return Object.freeze([]);
    const cineEvents = [];
    for (const event of retainedEvents) {
      if (!RETAINED_EVENT_ROWS.has(event)) return Object.freeze([]);
      const retained = snapshotDeeplyFrozenOrdinaryRecord(event);
      if (!retained || typeof retained.type !== 'string') return Object.freeze([]);
      if (retained.type.startsWith('cine-')) {
        if (retained.type !== 'cine-terminal' && !viewportCineController.ownsRow(event)) return Object.freeze([]);
        const snapshot = snapshotCineDrainEvent(event);
        if (!snapshot) return Object.freeze([]);
        cineEvents.push(snapshot);
      }
    }
    const grouped = new Map();
    for (const event of cineEvents) {
      const rows = grouped.get(event.cineId) || [];
      rows.push(event);
      grouped.set(event.cineId, rows);
    }
    const derivedAttempts = [];
    for (const group of grouped.values()) {
      const derived = deriveCineGroup(group);
      if (!derived) return Object.freeze([]);
      derivedAttempts.push(derived);
    }
    const attemptsByKey = new Map();
    for (const derived of derivedAttempts) {
      const attempt = derived.terminal ? derived.terminalBase : derived.proof;
      const key = `${attempt.originPhaseId}:${attempt.viewportIndex}:${attempt.interaction}:${attempt.direction}`;
      const attempts = attemptsByKey.get(key) || [];
      attempts.push(derived);
      attemptsByKey.set(key, attempts);
    }
    const proofs = [];
    const terminals = [];
    const work = [];
    for (const attempts of attemptsByKey.values()) {
      attempts.sort((left, right) => {
        const leftId = left.terminal ? left.terminalBase.cineId : left.proof.cineId;
        const rightId = right.terminal ? right.terminalBase.cineId : right.proof.cineId;
        return leftId - rightId;
      });
      const successfulSpans = attempts.filter(attempt => !attempt.terminal)
        .map(attempt => attempt.span);
      if (!successfulCineAttemptSpansAreStrictlySequential(successfulSpans)) {
        return Object.freeze([]);
      }
      let priorTerminalCineId = null;
      for (const [index, derived] of attempts.entries()) {
        if (index > attempts.length) return Object.freeze([]);
        if (derived.terminal) {
          const terminal = Object.freeze({
            cineId: derived.terminalBase.cineId,
            priorTerminalCineId,
            originPhaseId: derived.terminalBase.originPhaseId,
            viewportIndex: derived.terminalBase.viewportIndex,
            interaction: derived.terminalBase.interaction,
            direction: derived.terminalBase.direction,
            reason: derived.terminalBase.reason,
            terminalEventOrdinal: derived.terminalBase.terminalEventOrdinal,
            atMs: derived.terminalBase.atMs,
            events: derived.terminalBase.events,
          });
          terminals.push(terminal);
          priorTerminalCineId = terminal.cineId;
        } else {
          proofs.push(Object.freeze({ ...derived.proof, priorTerminalCineId }));
        }
        work.push(...derived.work);
      }
    }
    proofs.sort((left, right) => left.cineId - right.cineId);
    terminals.sort((left, right) => left.cineId - right.cineId);
    const frozenProofs = Object.freeze(proofs);
    const frozenTerminals = Object.freeze(terminals);
    const frozenWork = Object.freeze(work);
    DERIVED_CINE_PACKAGES.set(frozenProofs, {
      terminals: frozenTerminals,
      work: frozenWork,
      terminalExtracted: false,
    });
    return frozenProofs;
  } catch (_) {
    return Object.freeze([]);
  }
};

export const deriveViewportCineAttemptTerminals = proofs => {
  try {
    const derived = DERIVED_CINE_PACKAGES.get(proofs);
    if (!derived) return Object.freeze([]);
    if (derived.terminalExtracted) {
      DERIVED_CINE_PACKAGES.delete(proofs);
      return Object.freeze([]);
    }
    derived.terminalExtracted = true;
    return derived.terminals;
  } catch (_) {
    return Object.freeze([]);
  }
};

export const deriveViewportCinePhaseWork = (proofs, terminals) => {
  try {
    const derived = DERIVED_CINE_PACKAGES.get(proofs);
    if (!derived) return Object.freeze([]);
    DERIVED_CINE_PACKAGES.delete(proofs);
    if (!derived.terminalExtracted || terminals !== derived.terminals) return Object.freeze([]);
    return derived.work;
  } catch (_) {
    return Object.freeze([]);
  }
};

const snapshotWindowLevelDrainEvent = event => {
  try {
    const initial = snapshotDeeplyFrozenOrdinaryRecord(event);
    if (!initial || typeof initial.type !== 'string') return null;
    const type = initial.type;
    let keys = WINDOW_LEVEL_DRAIN_EVENT_KEYS[type];
    if (type === 'window-level-terminal') {
      if (initial.outcome === 'settled') keys = WINDOW_LEVEL_DRAIN_EVENT_KEYS['window-level-terminal-settled'];
      else if (initial.outcome === 'cancelled' || initial.outcome === 'failed') {
        keys = WINDOW_LEVEL_DRAIN_EVENT_KEYS['window-level-terminal-closed'];
      } else return null;
    }
    if (!keys) return null;
    const snapshot = snapshotDeeplyFrozenOrdinaryRecord(event, keys);
    if (!snapshot
        || !Number.isSafeInteger(snapshot.sequence) || snapshot.sequence <= 0
        || !Number.isSafeInteger(snapshot.interactionOrdinal) || snapshot.interactionOrdinal <= 0
        || snapshot.phase !== 'single-step'
        || !Number.isSafeInteger(snapshot.viewport) || snapshot.viewport < 0
        || !Number.isSafeInteger(snapshot.generation) || snapshot.generation <= 0
        || typeof snapshot.atMs !== 'number' || !Number.isFinite(snapshot.atMs) || snapshot.atMs < 0
        || typeof snapshot.ww !== 'number' || !Number.isFinite(snapshot.ww)
        || typeof snapshot.wc !== 'number' || !Number.isFinite(snapshot.wc)) return null;
    for (const ordinalKey of ['inputOrdinal', 'supersededByInputOrdinal', 'evidenceOrdinal']) {
      if (Object.prototype.hasOwnProperty.call(snapshot, ordinalKey)
          && (!Number.isSafeInteger(snapshot[ordinalKey]) || snapshot[ordinalKey] <= 0)) return null;
    }
    return snapshot;
  } catch (_) {
    return null;
  }
};

export const deriveViewportWindowLevelSamples = events => {
  try {
    // This reducer owns the pre-serialization drain boundary. Persisted JSON is
    // mutable and must first cross the report validator's owned-copy/deep-freeze
    // contract before it can be reduced here.
    // Weak branding rejects copies and proxies before any reflective operation.
    // Parsed reports are intentionally outside this pre-serialization reducer;
    // their validator must take an owned copy and validate that separate schema.
    if (!RETAINED_EVENT_DRAINS.has(events)) return Object.freeze([]);
    const retainedEvents = snapshotDeeplyFrozenOrdinaryArray(events);
    if (!retainedEvents) return Object.freeze([]);
    const windowLevelEvents = [];
    for (const event of retainedEvents) {
      if (!RETAINED_EVENT_ROWS.has(event)) return Object.freeze([]);
      const retained = snapshotDeeplyFrozenOrdinaryRecord(event);
      if (!retained || typeof retained.type !== 'string') return Object.freeze([]);
      if (retained.type.startsWith('window-level-')) {
        const snapshot = snapshotWindowLevelDrainEvent(event);
        if (!snapshot) return Object.freeze([]);
        windowLevelEvents.push(snapshot);
      }
    }
    const groups = new Map();
    for (const event of windowLevelEvents) {
      const group = groups.get(event.interactionOrdinal) || [];
      group.push(event);
      groups.set(event.interactionOrdinal, group);
    }
    const samples = [];
    const phaseKeys = new Set();
    for (const [interactionOrdinal, group] of groups) {
      group.sort((left, right) => left.sequence - right.sequence);
      if (group.some((event, index) => index > 0 && (event.sequence <= group[index - 1].sequence
          || event.atMs < group[index - 1].atMs))) return Object.freeze([]);
      const starts = group.filter(event => event.type === 'window-level-trusted-start');
      const terminals = group.filter(event => event.type === 'window-level-terminal');
      if (starts.length !== 1 || terminals.length !== 1 || terminals[0].outcome !== 'settled') continue;
      const terminal = terminals[0];
      if (!Number.isSafeInteger(terminal.inputOrdinal) || terminal.inputOrdinal <= 0
          || !Number.isSafeInteger(terminal.evidenceOrdinal) || terminal.evidenceOrdinal <= 0) return Object.freeze([]);
      const input = group.find(event => event.type === 'window-level-input'
        && event.inputOrdinal === terminal.inputOrdinal);
      const applied = group.filter(event => event.type === 'window-level-input-applied'
        && event.inputOrdinal === terminal.inputOrdinal);
      const publication = group.filter(event => event.type === 'window-level-render-publication'
        && event.inputOrdinal === terminal.inputOrdinal && event.evidenceOrdinal === terminal.evidenceOrdinal);
      const paint = group.filter(event => event.type === 'window-level-paint'
        && event.inputOrdinal === terminal.inputOrdinal && event.evidenceOrdinal === terminal.evidenceOrdinal);
      const release = group.filter(event => event.type === 'window-level-release'
        && event.inputOrdinal === terminal.inputOrdinal);
      const coalescedFinal = group.some(event => event.type === 'window-level-input-coalesced'
        && event.inputOrdinal === terminal.inputOrdinal);
      if (!input || applied.length !== 1 || publication.length !== 1 || paint.length !== 1
          || release.length !== 1 || coalescedFinal
          || !(starts[0].sequence < input.sequence && input.sequence < applied[0].sequence
            && applied[0].sequence < publication[0].sequence
            && publication[0].sequence < paint[0].sequence
            && input.sequence < release[0].sequence
            && Math.max(paint[0].sequence, release[0].sequence) < terminal.sequence)
          || ![applied[0], publication[0], paint[0], release[0], terminal]
            .every(event => event.ww === input.ww && event.wc === input.wc)) return Object.freeze([]);
      const nonFinalInputs = group.filter(event => event.type === 'window-level-input'
        && event.inputOrdinal !== terminal.inputOrdinal);
      if (nonFinalInputs.some(row => group.filter(event => event.type === 'window-level-input-coalesced'
        && event.inputOrdinal === row.inputOrdinal).length !== 1)) return Object.freeze([]);
      const phaseKey = `${starts[0].phase}:${starts[0].viewport}:${starts[0].generation}`;
      if (phaseKeys.has(phaseKey)) return Object.freeze([]);
      phaseKeys.add(phaseKey);
      samples.push(Object.freeze({
        interactionOrdinal,
        inputOrdinal: terminal.inputOrdinal,
        evidenceOrdinal: terminal.evidenceOrdinal,
        phase: starts[0].phase,
        viewport: starts[0].viewport,
        generation: starts[0].generation,
        startAtMs: starts[0].atMs,
        inputAtMs: input.atMs,
        appliedAtMs: applied[0].atMs,
        publicationAtMs: publication[0].atMs,
        paintAtMs: paint[0].atMs,
        releaseAtMs: release[0].atMs,
        settledAtMs: terminal.atMs,
        startWw: starts[0].ww,
        startWc: starts[0].wc,
        ww: input.ww,
        wc: input.wc,
      }));
    }
    return Object.freeze(samples);
  } catch (_) {
    return Object.freeze([]);
  }
};

let installedBrowserBenchmarkBridge = null;

export const isViewportEvidenceCinePhaseActive = () => {
  try {
    if (!isViewportEvidenceNetworkBridgeActive() || !installedBrowserBenchmarkBridge
        || typeof window === 'undefined') return false;
    const descriptor = Object.getOwnPropertyDescriptor(window, '__KAIROS_V8_EVIDENCE_BRIDGE__');
    if (!descriptor || descriptor.value !== installedBrowserBenchmarkBridge) return false;
    const snapshot = installedBrowserBenchmarkBridge.snapshot();
    return snapshot?.sessionActive === true
      && snapshot?.phaseActive === true
      && snapshot?.drained === false;
  } catch (_) {
    return false;
  }
};

export const installViewportEvidenceBrowserBenchmarkBridge = () => {
  try {
    if (typeof window === 'undefined' || !isViewportEvidenceBenchmarkRequested()) return false;
    const existing = Object.getOwnPropertyDescriptor(window, '__KAIROS_V8_EVIDENCE_BRIDGE__');
    if (existing) return existing.value === installedBrowserBenchmarkBridge;

    // This bridge owns one session and at most one phase. Both latches are
    // irreversible so end, drain, early close, and cleanup retries can never
    // allocate or install a replacement authority.
    const state = {
      session: null,
      phase: null,
      phaseConsumed: false,
      closed: false,
      drained: false,
    };
    const sessionIsActive = () => {
      try { return state.session?.snapshot?.().closed === false; } catch (_) { return false; }
    };
    const ensureSession = () => {
      if (state.closed) return false;
      if (sessionIsActive()) return true;
      if (state.session !== null) return false;
      const session = createViewportEvidenceReportSession();
      if (!installViewportEvidenceReportSession(session)) return false;
      state.session = session;
      state.phase = null;
      state.drained = false;
      return true;
    };
    if (!ensureSession()) return false;

    const bridge = Object.freeze({
      version: 2,
      snapshot: () => Object.freeze({
        sessionActive: sessionIsActive(),
        phaseActive: state.phase !== null,
        drained: state.drained,
      }),
      snapshotDiagnostics: (...args) => {
        if (args.length !== 0 || state.phase === null || state.closed || state.drained) return null;
        let phaseState = null;
        try { phaseState = CINE_PHASE_STATES.get(state.phase); } catch (_) { phaseState = null; }
        return snapshotCineDiagnostics(phaseState);
      },
      beginCinePhase: originPhaseId => {
        if (!Number.isSafeInteger(originPhaseId) || originPhaseId <= 0
            || state.closed || state.phaseConsumed || state.phase !== null
            || state.drained || !ensureSession()) return false;
        const phase = createViewportEvidenceCinePhase(state.session, { originPhaseId });
        if (!phase || !installViewportEvidenceCinePhase(phase)) return false;
        state.phase = phase;
        state.phaseConsumed = true;
        return true;
      },
      endCinePhase: () => {
        if (state.phase === null) return false;
        const phase = state.phase;
        let phaseState = null;
        try { phaseState = CINE_PHASE_STATES.get(phase); } catch (_) { phaseState = null; }
        if (!phaseState || !sealCinePhaseDiagnostics(phaseState)) {
          state.phase = null;
          uninstallViewportEvidenceCinePhase(phase);
          return false;
        }
        state.phase = null;
        return uninstallViewportEvidenceCinePhase(phase);
      },
      drainCinePhase: () => {
        if (!sessionIsActive() || !state.phaseConsumed
            || state.phase !== null || state.drained || state.closed) return null;
        let sessionState = null;
        try { sessionState = REPORT_SESSION_STATES.get(state.session); } catch (_) { sessionState = null; }
        const diagnostics = sessionState?.sealedCineDiagnostics;
        if (!diagnostics) return null;
        const events = state.session.drain();
        const proofs = deriveViewportCineProofs(events);
        const terminals = deriveViewportCineAttemptTerminals(proofs);
        const work = deriveViewportCinePhaseWork(proofs, terminals);
        state.drained = true;
        sessionState.sealedCineDiagnostics = null;
        return Object.freeze({ proofs, terminals, work, diagnostics });
      },
      closeSession: () => {
        state.closed = true;
        if (state.phase !== null) {
          const phase = state.phase;
          state.phase = null;
          uninstallViewportEvidenceCinePhase(phase);
        }
        if (!state.session) return false;
        const session = state.session;
        let before = null;
        try { before = session.snapshot(); } catch (_) { before = null; }
        let changed = false;
        try {
          changed = before?.closed === true
            ? session.close() === true
            : uninstallViewportEvidenceReportSession(session) === true;
        } catch (_) {
          changed = false;
        }
        let after = null;
        try { after = session.snapshot(); } catch (_) { after = null; }
        const complete = after?.closed === true
          && after.activeOperations === 0
          && after.activeCacheRoots === 0
          && after.pendingCleanup === 0;
        if (complete) state.session = null;
        return changed && complete;
      },
    });
    Object.defineProperty(window, '__KAIROS_V8_EVIDENCE_BRIDGE__', {
      configurable: false,
      enumerable: false,
      writable: false,
      value: bridge,
    });
    installedBrowserBenchmarkBridge = bridge;
    return true;
  } catch (_) {
    return false;
  }
};
